// web/src/tutorial/detail/beat-bloom.ts
// BeatBloom · 软件界面详细说明（按页签逐个参数）
//
// 定位：这是「参数手册」，不是入门流程。想先跑通一次，看 quickstart/beat-bloom.ts。
// 写法：按顶栏页签顺序，一屏一节；每节按截图上的卡片 / 分区拆成 subs，
//   每个可调参数都给出「作用 / 范围 / 默认 / 怎么调」，取值范围与默认值全部来自源码。
//
// 依据：H:\beat_bloom_rust
//   src/ui/app.rs（页签顺序与顶栏）、avatar_panel.rs（角色设置与保存流程）、
//   drummer_editor.rs（角色选择 / 编辑 / 删除对话框）、display_avatar_image.rs（角色预览）、
//   drum_kit_ui.rs（鼓组组件 / 可驱动肢体 / MIDI 触发器 / 特殊动作）、
//   data.rs（默认值）、midi_panel.rs（MIDI 处理）、execution_panel.rs（执行与产物）、
//   midi_merger_panel.rs（音频转 MIDI）、recorder_editor.rs（Recorder 编辑器）、
//   drum_pitch_table.rs（打击乐音高表）、src/midi/midi_processor.rs（通道 / 帧率 / 量化）、
//   src/midi_merger/midi_file.rs（打击乐类型识别）、src/limb/limb_state.rs（肢体系数）、
//   src/recorder/recorder_pool.rs（候选肢体组合规则）、
//   asset/i18n/zh-CN.json 与 en-US.json（界面原文）

import { l } from "../l";
import type { TutorialDoc } from "../types";

export const beatBloomDetail: TutorialDoc = {
  key: "beat_bloom",
  slug: "beat-bloom",
  kind: "detail",
  icon: "💥",
  title: l("BeatBloom 软件界面详细说明", "BeatBloom UI Reference"),
  subtitle: l(
    "按软件顶栏的五个页签，一屏一屏拆开讲：每个控件负责什么、取值范围和默认值是什么、什么时候需要调整，调整后界面会看到哪些变化。",
    "A screen-by-screen walkthrough of all five tabs: what each control does, its range and default, when you would want to change it, and what you see afterwards.",
  ),
  readMinutes: 14,
  video: {
    hidden: true,
    caption: l("本页不内嵌视频", "No embedded video on this page"),
  },
  images: [
    {
      src: "/assets/image/products/beat-bloom/CN/角色设置.png",
      srcEn: "/assets/image/products/beat-bloom/EN/Character Setting.png",
      label: l("角色设置", "Character Setting"),
      width: 1920,
      height: 1040,
    },
    {
      src: "/assets/image/products/beat-bloom/CN/MIDI处理.png",
      srcEn: "/assets/image/products/beat-bloom/EN/MIDI Processing.png",
      label: l("MIDI 处理", "MIDI Processing"),
      width: 902,
      height: 875,
    },
    {
      src: "/assets/image/products/beat-bloom/CN/执行操作.png",
      srcEn: "/assets/image/products/beat-bloom/EN/Execute.png",
      label: l("执行操作", "Execute"),
      width: 902,
      height: 875,
    },
    {
      src: "/assets/image/products/beat-bloom/CN/音频转MIDI.png",
      srcEn: "/assets/image/products/beat-bloom/EN/Audio to MIDI.png",
      label: l("音频转MIDI", "Audio to MIDI"),
      width: 902,
      height: 875,
    },
    {
      src: "/assets/image/products/beat-bloom/CN/Recorder编辑器.png",
      srcEn: "/assets/image/products/beat-bloom/EN/Recorder Editor.png",
      label: l("Recorder 编辑器", "Recorder Editor"),
      width: 902,
      height: 875,
    },
  ],
  sections: [
    // ─────────────────────────────────────────────────────────────
    {
      id: "how-to-read",
      title: l("这篇怎么用", "How to use this page"),
      image: { image: 0 },
      blocks: [
        {
          kind: "p",
          text: l(
            "本页是**参数手册**：按软件顶栏的五个页签顺序（⚙ 角色设置 → 🎵 MIDI 处理 → ▶ 执行操作 → 🎼 音频转MIDI → 📝 Recorder 编辑器），一屏一屏地讲每个分区、每个按钮和每一个可调参数。想先跑通一次完整流程，请看[快速上手](/quickstart/beat-bloom.html)。",
            "This page is a **reference**: it walks the five tabs in the order they appear in the app (⚙ Character Settings → 🎵 MIDI Processing → ▶ Execute → 🎼 Audio to MIDI → 📝 Recorder Editor), explaining every section, every button and every adjustable parameter. To get through the flow once first, read the [Quick Start](/quickstart/beat-bloom.html).",
          ),
        },
        {
          kind: "p",
          text: l(
            "右下角悬浮窗显示的是对应页签的真实截图，正文滚到哪一节，窗口就自动换到那一屏。软件的顶栏有 语言 菜单（含 跟随系统）与 暗色主题 勾选框，切换语言后界面文字与截图都会跟着变。",
            "The floating window in the bottom-right shows the real screenshot of the tab being discussed; scroll into a section and it switches to that screen automatically. The top bar has a 语言 / Language menu (including 跟随系统 / Follow system) and a 暗色主题 / Dark theme checkbox, and both the UI text and the screenshots follow the language you pick.",
          ),
        },
        {
          kind: "note",
          text: l(
            "正文里用反引号包起来的都是界面上真实存在的标签，例如 ⚙ 角色设置、帧率:、添加触发器。范围、默认值、以及每一条提示文案都取自软件当前版本。",
            "Anything in backticks is a real label in the app, such as ⚙ Character Settings, FPS: or Add Trigger. Ranges, defaults and every quoted message come from the current version of the software.",
          ),
        },
        {
          kind: "note",
          text: l(
            "截图覆盖的是五个页签的主界面。几个从主界面弹出来的小界面没有单独截图：「编辑角色信息」卡片、⚠️ 确认删除 确认窗口、以及 帮助 → 打击乐音高表 打开的独立窗口，文中都用文字讲清楚了。",
            "The screenshots cover the five main tabs. A few smaller surfaces opened from them have no screenshot of their own — the 编辑角色信息 / Edit Character Info card, the ⚠️ 确认删除 / ⚠️ Confirm Delete dialog, and the separate window opened by 帮助 / Help → 打击乐音高表 / Drum Pitch Table — and the text describes each of them.",
          ),
        },
      ],
    },

    // ─────────────────────────────────────────────────────────────
    {
      id: "tab-character",
      title: l("页签 1：角色设置", "Tab 1 — Character Settings"),
      image: { image: 0 },
      blocks: [
        {
          kind: "p",
          text: l(
            "第一个页签在界面上叫「角色设置」，它其实就是本产品的**演奏者设置页**——这里的「角色」指鼓手。整页分上下两块：上半左右两栏（左栏是「角色选择」卡片 + 「操作」卡片 + Head_Control 阈值，右栏是「角色预览」卡片），下半是整行的「鼓组信息」。",
            "The first tab is labelled 角色设置 / Character Settings, and it **is this product's performer setup page** — 角色 / character here means the drummer. The page has two halves: a two-column top (left: the 角色选择 / Character Select card, the 操作 / Actions card and the Head_Control Threshold; right: the 角色预览 / Character Preview card) and a full-width 鼓组信息 / Drum Kit Info block below.",
          ),
        },
        {
          kind: "callout",
          tone: "info",
          title: l(
            "选人只影响「谁在打」",
            "Selecting a performer only decides who plays",
          ),
          text: l(
            "这一页的两套东西要分清楚：上半部分（角色选择 / 操作 / 角色预览）改的是 asset/drumers.json，也就是「软件里登记了哪些鼓手、各自挂了哪份配置、头像是哪张图」；下半部分「鼓组信息」改的是 asset/drum_kit/*.drumkit，也就是「这份鼓组里有哪些鼓件、每个鼓件认哪些音高、能被哪些肢体打」。两套改动各有各的保存按钮。",
            "Two different things live on this page. The top half (character select, actions, preview) edits asset/drumers.json — which drummers are registered, which profile each one is attached to, and which image it uses. The 鼓组信息 / Drum Kit Info block below edits asset/drum_kit/*.drumkit — which pieces the kit has, which pitches each piece answers to, and which limbs may hit it. Each half has its own save button.",
          ),
        },
      ],
      subs: [
        {
          id: "character-select",
          title: l("角色选择：用哪个鼓手", "Character select: which drummer"),
          blocks: [
            {
              kind: "p",
              text: l(
                "左栏最上面的卡片是「角色选择」（副标题「从下拉列表中选择要编辑的角色」）。它决定接下来所有页签里「谁在打」。",
                "The top card in the left column is 角色选择 / Character Select (subtitle “Select the character to edit from the dropdown”). It decides who plays for every other tab.",
              ),
            },
            {
              kind: "ul",
              items: [
                l(
                  "**下拉菜单**列出 asset/drumers.json 里登记的全部鼓手。软件启动时会自动选中第一条并加载它的配置，所以第一次打开通常已经有人选着；列表为空时下拉框显示占位文字 请选择角色...。",
                  "**The dropdown** lists every drummer registered in asset/drumers.json. On startup the first entry is selected and its profile loaded, so there is usually already someone selected; with an empty list the box shows the placeholder 请选择角色 / Select a character....",
                ),
                l(
                  "**切换鼓手会立刻生效**：软件马上读取这条登记指向的 .drumkit，把「可用肢体」和下方整块「鼓组信息」一起刷新。",
                  "**Switching takes effect immediately**: the app reads the .drumkit that entry points at and refreshes both 可用肢体 / Available Limbs and the whole 鼓组信息 / Drum Kit Info block.",
                ),
                l(
                  "**可用肢体**：这张内嵌卡片把鼓组配置里的 limbs 用 chip 列出来（自带鼓手是 left_hand / right_hand / left_foot / right_foot）。它不是软件写死的，而是每个鼓组自己的字段——某个鼓组少了 left_foot，这个鼓手就永远不会用左脚踩踏板。",
                  "**可用肢体 / Available Limbs**: this inset card lists the kit profile's limbs as chips (bundled drummers show left_hand / right_hand / left_foot / right_foot). It is not hard-coded — it is a field of each kit. If a kit has no left_foot, that drummer will never put the left foot on the pedal.",
                ),
              ],
            },
            {
              kind: "note",
              text: l(
                "「可用肢体」这一栏只能看，不能改。要增删肢体，得改对应的 .drumkit 文件（或在下方「鼓组信息」里给鼓件重新配 可驱动肢体）。",
                "可用肢体 / Available Limbs is read-only. To add or remove a limb you have to edit the .drumkit file itself (or re-assign 可驱动肢体 / Drivable Limbs per piece below).",
              ),
            },
          ],
        },
        {
          id: "character-actions",
          title: l("操作：编辑、新增、删除鼓手", "Actions: edit, add, delete"),
          blocks: [
            {
              kind: "p",
              text: l(
                "「角色选择」下面是「操作」卡片，三个按钮都在这儿。所有改动都会写回 asset/drumers.json。",
                "Under 角色选择 / Character Select sits the 操作 / Actions card with all three buttons. Every change is written back to asset/drumers.json.",
              ),
            },
            {
              kind: "table",
              head: [l("按钮", "Button"), l("点了会发生什么", "What it does")],
              rows: [
                [
                  l("🔧 编辑鼓手", "🔧 Edit Drummer"),
                  l(
                    "把卡片换成「编辑角色信息」（副标题「修改角色的基本信息」），进入编辑态。只有已经选中鼓手时才显示。",
                    "Swaps the card for 编辑角色信息 / Edit Character Info (subtitle “Modify basic character info”) and enters edit mode. Only shown when a drummer is selected.",
                  ),
                ],
                [
                  l("➕ 新增鼓手", "➕ Add Drummer"),
                  l(
                    "立刻新建一个名为「新鼓手」的角色（这个名字在软件里是写死的中文），配置从列表第一个鼓手**复制**一份，生成 新鼓手.drummer / 新鼓手.drumkit，随后自动进入编辑态并提示「已新增鼓手，请修改名称后保存」。列表原本为空时会直接创建一份空鼓组。",
                    "Immediately creates a performer named 新鼓手 (that name is hard-coded in Chinese inside the app), **copying** the profiles of the first drummer in the list into 新鼓手.drummer / 新鼓手.drumkit, then drops you into edit mode with the hint 已新增鼓手，请修改名称后保存 / Drummer added. Edit the name and save. With an empty list it creates an empty kit instead.",
                  ),
                ],
                [
                  l("🗑 删除鼓手", "🗑 Delete Drummer"),
                  l(
                    '弹出居中的 ⚠️ 确认删除 窗口，写明「确定要删除鼓手 "名字" 吗？」与「此操作不可撤销。」；只有点 ✅ 确认删除 才真的执行。删除只从 asset/drumers.json 里移除这条登记，磁盘上的 .drummer / .drumkit 文件不会被删掉。',
                    'Opens the centred ⚠️ Confirm Delete window reading “Are you sure you want to delete drummer "name"?” and “This action cannot be undone.”; only ✅ Confirm Delete proceeds. Deletion removes the entry from asset/drumers.json — the .drummer / .drumkit files stay on disk.',
                  ),
                ],
                [
                  l("❌ 取消 / 💾 保存", "❌ Cancel / 💾 Save"),
                  l(
                    "只在编辑态出现。💾 保存 写回 drumers.json；❌ 取消 放弃这次改动。",
                    "Only present in edit mode. 💾 Save writes back to drumers.json; ❌ Cancel discards the changes.",
                  ),
                ],
              ],
            },
            {
              kind: "p",
              text: l(
                "编辑态里能改的东西不多，但每一项都值得看一眼：",
                "Edit mode does not offer much, but each field is worth a look:",
              ),
            },
            {
              kind: "ul",
              items: [
                l(
                  "角色名称: —— 这个名字会出现在输出文件名里（output/recorder/{角色名}_{MIDI名}_{轨道}.recorder），所以改名字等于换了结果文件的命名。",
                  "角色名称 / Character name: — this name ends up in the output file names (output/recorder/{performer}_{midi}_{track}.recorder), so renaming changes how results are named.",
                ),
                l(
                  "Drummer 文件: 与 DrumKit 文件: —— 显示当前挂的配置文件名，右边的 选择 .drummer / 选择 .drumkit 可以换一个。扩展名不对会自动补成 .drummer / .drumkit；从别处选的文件会被复制进 asset/avatar/ 或 asset/drum_kit/。**这两个标签在英文界面里也是中文**（软件里写死为 Drummer 文件: / DrumKit 文件:）。",
                  "Drummer 文件: and DrumKit 文件: — show the attached profile names, and 选择 .drummer / 选择 .drumkit let you attach different ones. A wrong extension is corrected to .drummer / .drumkit; a file picked from elsewhere is copied into asset/avatar/ or asset/drum_kit/. **These two labels stay Chinese even in the English UI** (they are hard-coded as Drummer 文件: / DrumKit 文件:).",
                ),
                l(
                  "挂文件时底部的绿色横幅会显示「已选择鼓手文件: xxx」或「已选择鼓组文件: xxx」——这两条也是写死的中文。",
                  "When you attach a file, the green banner shows “已选择鼓手文件: xxx” or “已选择鼓组文件: xxx” — these two messages are hard-coded Chinese as well.",
                ),
              ],
            },
            {
              kind: "callout",
              tone: "info",
              title: l("保存结果在哪看", "Where the save result appears"),
              text: l(
                "任何一种保存都会在页面底部弹一条绿色横幅，3 秒后自动消失（右侧的 × 可以立刻关掉）。常见文案：「鼓手信息已保存」「成功删除鼓手」「已新增鼓手，请修改名称后保存」「配置已保存至: asset/drum_kit/xxx.drumkit」；失败时是「保存失败: 原因」「删除鼓手失败: 原因」「新增鼓手失败: 原因」。",
                "Every save pops a green banner at the bottom that disappears after three seconds (the × on the right dismisses it at once). Typical texts: “Drummer info saved”, “Drummer deleted”, “Drummer added. Edit the name and save”, “Config saved to: asset/drum_kit/xxx.drumkit”; failures read “Failed to save: reason”, “Failed to delete drummer: reason” or “Failed to add drummer: reason”.",
              ),
            },
            {
              kind: "callout",
              tone: "tip",
              text: l(
                "这一页只是「登记与选择」：把一个已有的 .drummer / .drumkit 挂到界面上。真正把一套新的人物骨骼做成可用的角色数据，见《自定义人物进阶教程》。",
                "This page only registers and selects: it attaches existing .drummer / .drumkit profiles to the UI. Turning a brand-new rig into usable performer data is covered by the Custom Performer tutorial.",
              ),
            },
          ],
        },
        {
          id: "character-preview",
          title: l("角色预览：换一张头像", "Character preview: swap the image"),
          blocks: [
            {
              kind: "p",
              text: l(
                "右栏整块是「角色预览」卡片（副标题「头像资源将同步写回鼓手配置」）。它只做一件事：换这个鼓手的预览图。图片显示上限 180×180 像素，当前文件名写在图片下方，格式是 图片文件: default.png。",
                "The whole right column is the 角色预览 / Character Preview card (subtitle “The avatar resource will be synced back to the drummer config”). It does one thing: swap this drummer's preview image. The image is shown at up to 180×180 px, with its file name below in the form 图片文件 / Image file: default.png.",
              ),
            },
            {
              kind: "table",
              head: [l("控件", "Control"), l("作用", "What it does")],
              rows: [
                [
                  l("更换图片", "Change Image"),
                  l(
                    "进入编辑态，按钮行换成下面三个。",
                    "Enters edit mode; the button row is replaced by the three below.",
                  ),
                ],
                [
                  l("选择图片", "Select Image"),
                  l(
                    "打开文件对话框，过滤器是 png / gif / jpg / jpeg。选中的图片会被复制进 asset/img/，然后在窗口里预览。",
                    "Opens a file dialog filtered to png / gif / jpg / jpeg. The chosen image is copied into asset/img/ and previewed in the card.",
                  ),
                ],
                [
                  l("保存图片", "Save Image"),
                  l(
                    "把新文件名写回 drumers.json 并退出编辑态，绿色横幅随之出现。",
                    "Writes the new file name back to drumers.json, leaves edit mode, and raises the green banner.",
                  ),
                ],
                [
                  l("取消", "Cancel"),
                  l(
                    "放弃这次换图，回到原来的图片。",
                    "Discards the change and returns to the previous image.",
                  ),
                ],
              ],
            },
            {
              kind: "note",
              text: l(
                "图片读不到时软件会退回 asset/img/default.png；连默认图都读不到时卡片里显示「图片未找到」。换图不影响动画生成，纯粹是这一屏好看。",
                "If the image cannot be read the app falls back to asset/img/default.png, and if even that fails the card shows 图片未找到 / Image not found. Swapping the image has no effect on generation — it is purely cosmetic here.",
              ),
            },
          ],
        },
        {
          id: "head-control",
          title: l(
            "Head_Control 阈值：头部关键帧密度",
            "Head_Control Threshold: head keyframe density",
          ),
          blocks: [
            {
              kind: "p",
              text: l(
                "「角色选择」/「操作」下面还有一行，是本页唯一一个数值型动作参数。控件行写作 更新阈值: 加一个带 % 后缀的滑杆，上方标着 Head_Control 阈值。",
                "Below the select and action cards sits the page's only numeric motion parameter. The control row reads 更新阈值 / Update threshold: followed by a slider with a % suffix, labelled Head_Control 阈值 / Head_Control Threshold above.",
              ),
            },
            {
              kind: "table",
              head: [
                l("参数", "Parameter"),
                l("作用", "What it does"),
                l("范围", "Range"),
                l("默认", "Default"),
                l("怎么调", "How to tune"),
              ],
              rows: [
                [
                  l(
                    "Head_Control 阈值（控件行写作 更新阈值:）",
                    "Head_Control Threshold (control row labelled Update threshold:)",
                  ),
                  l(
                    "控制头部朝向动画的关键帧密度：软件把双手的中点映射到头部控制器位置，再按这个阈值决定哪些帧需要真正写入关键帧。",
                    "Controls the keyframe density of the head-orientation animation: the midpoint of both hands is mapped onto the head controller, and this threshold decides which frames actually get a keyframe.",
                  ),
                  l("2% – 100%", "2% – 100%"),
                  l("100%", "100%"),
                  l(
                    "界面提示原文是「阈值越大，关键帧越密集（0%=不敏感，100%=非常敏感）」。默认已经是最敏感的一档；只有头部动作明显过于活跃、或者结果文件大到不好处理时才往下调——往下调头部动作会更迟滞。",
                    "The hint under the slider reads 阈值越大，关键帧越密集 / “Higher threshold means denser keyframes” (0% = insensitive, 100% = very sensitive). The default is already the most sensitive step; lower it only when the head motion is visibly too busy or the result file is unwieldy — a lower value makes the head move more sluggishly.",
                  ),
                ],
              ],
            },
            {
              kind: "note",
              text: l(
                "滑杆实际能拖的范围是 2%–100%（最小值不是 0），而提示文案里写的是 0%。",
                "The slider actually stops at 2%–100% (the minimum is not 0), while the hint text mentions 0%.",
              ),
            },
          ],
        },
        {
          id: "drum-kit-components",
          title: l("鼓组信息 · 鼓组组件", "Drum Kit Info · components"),
          blocks: [
            {
              kind: "p",
              text: l(
                "页面下半部分整行是「鼓组信息」卡片（副标题「配置鼓组组件和特殊动作」）。里面分两大块：上半「鼓组组件」（副标题「配置各个鼓组部件」），下半「特殊动作」（副标题「配置特殊演奏动作」）。截图这一屏只能看到「鼓组组件」的开头，往下滚才是完整列表。",
                "The full-width block at the bottom of the page is the 鼓组信息 / Drum Kit Info card (subtitle “Configure drum kit components and special actions”). It has two parts: 鼓组组件 / Drum Kit Components on top (subtitle “Configure each drum kit part”) and 特殊动作 / Special Actions below (subtitle “Configure special performance actions”). The screenshot only catches the beginning of the component list — scroll down for the rest.",
              ),
            },
            {
              kind: "p",
              text: l(
                "一个「鼓组组件」就是**一个鼓件**。它同时决定三件事：哪些音高进来时由它认领（MIDI 触发器）、它能被哪些肢体打到（可驱动肢体）、以及它的名字（会出现在执行页的 鼓组部件: 回显、控制台的映射统计和形状键动画的分组名里）。",
                "One component is **one drum piece**. It decides three things at once: which incoming pitches it claims (MIDI 触发器 / MIDI Triggers), which limbs may hit it (可驱动肢体 / Drivable Limbs), and its name — which shows up in the 鼓组部件 / Drum kit parts: echo, in the console's mapping statistics and as the group name in the shape-key animation.",
              ),
            },
            {
              kind: "table",
              head: [l("控件", "Control"), l("点了会发生什么", "What it does")],
              rows: [
                [
                  l("➕ 新增组件", "➕ Add Component"),
                  l(
                    "在列表末尾加一个鼓件，名字默认「新组件」、描述默认「组件描述」（这两个默认值在软件里是写死的中文），并自动展开。",
                    "Appends a piece named 新组件 with the description 组件描述 (both defaults are hard-coded Chinese in the app) and expands it.",
                  ),
                ],
                [
                  l("− / +", "− / +"),
                  l(
                    "折叠 / 展开这一个鼓件的详情。收起时只占一行（只显示名字与 删除）。",
                    "Collapses or expands that piece's details. Collapsed, it takes a single row showing just the name and 删除 / Delete.",
                  ),
                ],
                [
                  l("删除", "Delete"),
                  l(
                    "从这份鼓组配置里删掉该鼓件。它只是从界面上的列表里去掉，要再点一次页面最下方的 💾 保存鼓组配置 才真正落盘。",
                    "Removes the piece from this kit profile. It only disappears from the list — it reaches disk when you click 💾 保存鼓组配置 / 💾 Save Drum Kit Config at the bottom of the page.",
                  ),
                ],
                [
                  l("名称:", "Name:"),
                  l(
                    "鼓件名。**不能包含空格**：输入框下方会立刻弹出带 ⚠ 的「名称不能包含空格」，保存时也会被拦下。自带鼓件写成 Right_Kick_drum、Open_Hi-Hat 这种形式，用下划线或连字符代替空格。",
                    "The piece name. **Spaces are not allowed**: a ⚠ 名称不能包含空格 / Names cannot contain spaces warning appears right below the field, and saving is blocked. Bundled pieces are written as Right_Kick_drum, Open_Hi-Hat and so on, using underscores or hyphens instead of spaces.",
                  ),
                ],
                [
                  l("描述:", "Description:"),
                  l(
                    "多行备注，只给人看，不参与计算。",
                    "A multi-line note for humans only; it takes no part in the computation.",
                  ),
                ],
              ],
            },
          ],
        },
        {
          id: "drum-kit-limbs",
          title: l(
            "鼓组信息 · 可驱动肢体与系数",
            "Drum Kit Info · drivable limbs and coefficients",
          ),
          blocks: [
            {
              kind: "p",
              text: l(
                "展开一个鼓件后，右栏第一块是 可驱动肢体，回答「这个鼓件能被谁打」。每一行是一条肢体 chip 加一个数值框。",
                "Inside an expanded piece, the first block on the right is 可驱动肢体 / Drivable Limbs, answering “who may hit this piece”. Each row is a limb chip plus a number field.",
              ),
            },
            {
              kind: "table",
              head: [
                l("参数", "Parameter"),
                l("作用", "What it does"),
                l("范围", "Range"),
                l("默认", "Default"),
                l("怎么调", "How to tune"),
              ],
              rows: [
                [
                  l("肢体 chip + 系数数值框", "Limb chip + coefficient field"),
                  l(
                    "一行 = 一条能打这个鼓件的肢体，以及它的系数。行尾的 × 删掉这一行（只剩一行时不显示）。",
                    "One row = one limb that may hit this piece, plus its coefficient. The × at the end of the row removes it (hidden when only one row remains).",
                  ),
                  l(
                    "系数用拖动改，步进 0.1（软件不设上下限；自带鼓组用 1.0 / 1.2 两档，个别鼓件用 1.5）",
                    "The coefficient is dragged, stepping by 0.1 (the app sets no bounds; bundled kits use 1.0 and 1.2, and 1.5 on a few pieces)",
                  ),
                  l("新增一行时 1.0", "1.0 when a row is added"),
                  l(
                    "系数不是优先级，而是**惩罚乘数**：软件计算「这条肢体打这个鼓件」的代价时，会把「系数 × 这条肢体的基准移动距离」加进去。所以 1.0 是中性，1.1 / 1.2 表示尽量别用这条肢体打这个鼓。想固定由右手打某个鼓件，就把左手系数抬到 1.1 左右、右手保持 1.0。",
                    "The coefficient is not a priority but a **penalty multiplier**: when costing “this limb hits this piece”, the app adds “coefficient × this limb's baseline travel distance”. So 1.0 is neutral and 1.1 / 1.2 tells the solver to avoid that limb for that piece. To pin a piece to the right hand, raise its left-hand coefficient to about 1.1 and leave the right hand at 1.0.",
                  ),
                ],
                [
                  l("选择肢体", "Select limb"),
                  l(
                    "从鼓组的 limbs 里挑一条加进来。已经在列表里的肢体不会被重复添加；鼓组本身没有肢体时这个下拉不会出现。",
                    "Adds a limb from the kit's limbs. Limbs already listed are not added twice; the dropdown does not appear at all when the kit defines no limbs.",
                  ),
                  l("鼓组 limbs 里的四条", "The kit's limbs entries"),
                  l("无", "None"),
                  l(
                    "自带鼓组都是 left_hand / right_hand / left_foot / right_foot，普通鼓件列 1–2 条，踩镲这类可以列到手和脚。",
                    "Bundled kits use left_hand / right_hand / left_foot / right_foot. Ordinary pieces list one or two; a hi-hat may list both hands and a foot.",
                  ),
                ],
              ],
            },
            {
              kind: "callout",
              tone: "info",
              title: l(
                "手和脚可以同时打同一个鼓件",
                "A hand and a foot may share a piece",
              ),
              text: l(
                "枚举候选方案时软件遵守一条规则：**手和脚可以同时打同一个鼓件，但两只手（或两只脚）不能同时打同一个**。所以一个踩镲可以同时列出左手、右手和左脚，而左脚 + 右脚同时踩同一个踏板这种组合会被直接排除。",
                "When enumerating candidates the app follows one rule: **a hand and a foot may hit the same piece at once, but two hands (or two feet) may not**. A hi-hat can therefore list left hand, right hand and left foot together, while left foot + right foot on the same pedal is excluded outright.",
              ),
            },
          ],
        },
        {
          id: "drum-kit-triggers",
          title: l("鼓组信息 · MIDI 触发器", "Drum Kit Info · MIDI triggers"),
          blocks: [
            {
              kind: "p",
              text: l(
                "展开鼓件的下半部分是 MIDI 触发器，这里才是真正建立映射的地方：**这个鼓件响应哪些 MIDI 音高**。MIDI 里出现的音高如果没有任何触发器认领，它就完全不产生动作。",
                "The lower half of an expanded piece is MIDI 触发器 / MIDI Triggers — the heart of the mapping: **which MIDI pitches this piece answers to**. A pitch that no trigger claims produces no motion at all.",
              ),
            },
            {
              kind: "table",
              head: [
                l("参数", "Parameter"),
                l("作用", "What it does"),
                l("范围", "Range"),
                l("默认", "Default"),
                l("怎么调", "How to tune"),
              ],
              rows: [
                [
                  l("音高数值框", "Pitch field"),
                  l(
                    "这一条触发器认领的一个 MIDI 音高。同一个音高可以被多个鼓件认领，软件会自己挑代价最低的那个。",
                    "One MIDI pitch claimed by this trigger. Several pieces may claim the same pitch; the solver picks the cheapest one.",
                  ),
                  l("0 – 128", "0 – 128"),
                  l("新建条目时为 0", "0 on a new entry"),
                  l(
                    "对着顶栏 帮助 → 打击乐音高表 查。那张表按 FL Studio 的导出顺序排列，音高范围 27–87，Bass Drum 1 在 36、Acoustic Snare 在 38、Closed Hi-Hat 在 42。",
                    "Look it up under 帮助 / Help → 打击乐音高表 / Drum Pitch Table. That table follows FL Studio's export order, pitches run 27–87, with Bass Drum 1 at 36, Acoustic Snare at 38 and Closed Hi-Hat at 42.",
                  ),
                ],
                [
                  l("声音名文本框", "Sound-name box"),
                  l(
                    "这条音高的名字，只出现在控制台的映射统计里（「映射的component如下:」那一段）。它不参与计算。",
                    "A name for this pitch. It only shows up in the console's mapping statistics (the “Mapped components:” block) and takes no part in the computation.",
                  ),
                  l("任意文本", "Any text"),
                  l("新建条目时为「新声音」", "新声音 on a new entry"),
                  l(
                    "建议照抄音高表里的英文名（例如 Acoustic Snare）。排查未映射音符时，名字比数字好认。",
                    "Copying the English name from the pitch table (say Acoustic Snare) pays off: names are easier to read than numbers when you are chasing unmapped notes.",
                  ),
                ],
                [
                  l("×", "×"),
                  l(
                    "删掉这一条触发器（只剩一条时不显示）。",
                    "Removes this trigger (hidden when only one row remains).",
                  ),
                  l("—", "—"),
                  l("—", "—"),
                  l(
                    "删掉全部触发器等于告诉软件「这个鼓件不认任何音高」，它也就永远不会有动作。",
                    "Removing every trigger tells the app this piece answers to no pitch — it will then never move.",
                  ),
                ],
                [
                  l("添加触发器", "Add Trigger"),
                  l(
                    "在触发器列表末尾追加一条。列表里的音高是不重复的：音高已经存在时不会被加进去。",
                    "Appends one entry to the trigger list. Pitches in the list are unique — a pitch that already exists is not added.",
                  ),
                  l("—", "—"),
                  l(
                    "新增条目为「音高 0 / 声音 新声音」",
                    "A new entry is pitch 0 / sound 新声音",
                  ),
                  l(
                    "新条目的音高默认是 0。软件没有把 添加触发器 左边那个音高框的值保存下来，所以实际用法是：先加一条，再在列表里把这条的音高改成你在音高表里查到的值。",
                    "A new entry defaults to pitch 0. The app does not keep the value of the pitch box next to 添加触发器 / Add Trigger, so in practice: add the entry, then edit its pitch in the list to the value you looked up.",
                  ),
                ],
              ],
            },
            {
              kind: "ol",
              items: [
                l(
                  "打开顶栏 帮助 → 打击乐音高表，查到目标鼓件对应的音高。",
                  "Open 帮助 / Help → 打击乐音高表 / Drum Pitch Table and look up the pitch for your piece.",
                ),
                l(
                  "回「鼓组组件」，对目标鼓件点 + 展开，在 MIDI 触发器 一栏点 添加触发器。",
                  "Back in 鼓组组件 / Drum Kit Components, expand the piece with + and click 添加触发器 / Add Trigger.",
                ),
                l(
                  "把新条目的音高改成查到的值，声音名改成表里的英文名。",
                  "Change the new entry's pitch to that value and its sound name to the English name from the table.",
                ),
                l(
                  "如果这个鼓件还要被别的肢体打，在 可驱动肢体 里用 选择肢体 补上，并按上一节的规则给系数。",
                  "If other limbs should reach this piece too, add them with 选择肢体 / Select limb under 可驱动肢体 / Drivable Limbs and set coefficients as described above.",
                ),
                l(
                  "点页面最下面的 💾 保存鼓组配置。成功会提示「配置已保存至: asset/drum_kit/xxx.drumkit」。",
                  "Click 💾 保存鼓组配置 / 💾 Save Drum Kit Config at the bottom. Success reads “Config saved to: asset/drum_kit/xxx.drumkit”.",
                ),
                l(
                  "回 ▶ 执行操作 重新 ⚡ 一键生成。映射是生成阶段读进去的，不重新生成不会生效。",
                  "Return to ▶ Execute and run ⚡ Generate All (MIDI → Recorder → Animation) again. The mapping is read at generation time; without a re-run nothing changes.",
                ),
              ],
            },
            {
              kind: "note",
              text: l(
                "鼓组配置属于哪个角色，由 asset/drumers.json 里那条记录的 drum_kit_info 字段决定，**两个角色可以共用同一份 .drumkit**（自带的鼓手里就有这种写法）。所以改鼓组之前，先确认自己改的是哪个角色挂的那份文件。",
                "Which performer a kit belongs to is decided by the drum_kit_info field of its record in asset/drumers.json, and **two performers may share one .drumkit** (the bundled drummers already do). Before editing a kit, confirm which file the current performer is attached to.",
              ),
            },
          ],
        },
        {
          id: "drum-kit-actions",
          title: l("鼓组信息 · 特殊动作", "Drum Kit Info · special actions"),
          blocks: [
            {
              kind: "p",
              text: l(
                "「鼓组组件」下面是「特殊动作」（副标题「配置特殊演奏动作」）。它的结构和鼓件几乎一样，用来描述「同一个动作要几条肢体一起做」这类事情，自带鼓组里就有一条 Sticks（音高 31）。",
                "Below the components sits 特殊动作 / Special Actions (subtitle “Configure special performance actions”). Its structure mirrors a piece and it describes things where several limbs move as one — a bundled kit ships with a Sticks entry at pitch 31.",
              ),
            },
            {
              kind: "table",
              head: [l("控件", "Control"), l("作用", "What it does")],
              rows: [
                [
                  l("➕ 新增动作", "➕ Add Action"),
                  l(
                    "追加一条动作，名字默认「新动作」、描述默认「动作描述」（同样是写死的中文），并自动展开。",
                    "Appends an action named 新动作 with the description 动作描述 (also hard-coded Chinese) and expands it.",
                  ),
                ],
                [
                  l("名称: / 描述:", "Name: / Description:"),
                  l(
                    "含义与鼓件完全相同；名称: 同样不能带空格。",
                    "Identical to a piece; 名称 / Name: also rejects spaces.",
                  ),
                ],
                [
                  l("使用肢体", "Used Limbs"),
                  l(
                    "与鼓件的 可驱动肢体 唯一的结构区别：这里**只有肢体列表，没有系数**。下方的 选择肢体 同样从鼓组的 limbs 里挑。",
                    "One structural difference from 可驱动肢体 / Drivable Limbs: this list **has no coefficients**. The 选择肢体 / Select limb dropdown beneath it draws from the kit's limbs just the same.",
                  ),
                ],
                [
                  l("MIDI 触发器", "MIDI Triggers"),
                  l(
                    "与鼓件完全一致：音高数值框（0–128）+ 声音名文本框 + × 删除 + 添加触发器。",
                    "Exactly like a piece: pitch field (0 – 128), sound-name box, × to remove, and 添加触发器 / Add Trigger.",
                  ),
                ],
                [
                  l("删除", "Delete"),
                  l(
                    "删掉这一条动作，同样要保存鼓组配置才落盘。",
                    "Removes the action; again it only reaches disk once you save the kit config.",
                  ),
                ],
              ],
            },
            {
              kind: "callout",
              tone: "warn",
              title: l(
                "特殊动作目前不参与生成",
                "Special actions are not used by generation",
              ),
              text: l(
                "生成阶段只读取「鼓组组件」的 MIDI 触发器 与 可驱动肢体；「特殊动作」目前只会写进 .drumkit 文件，不会改变动画结果。所以要补音高、调肢体，请在「鼓组组件」里做。",
                "Generation only reads the MIDI 触发器 / MIDI Triggers and 可驱动肢体 / Drivable Limbs of the **components**; special actions are currently written into the .drumkit file and nothing else — they do not change the animation. So add pitches and adjust limbs on the components, not here.",
              ),
            },
          ],
        },
        {
          id: "drum-kit-save",
          title: l(
            "鼓组信息 · 保存与校验",
            "Drum Kit Info · saving and validation",
          ),
          blocks: [
            {
              kind: "p",
              text: l(
                "鼓组里的所有改动都是先在界面上改，最后必须点一次页面最下方的 💾 保存鼓组配置 才写进 asset/drum_kit/*.drumkit。",
                "Every kit edit is made in the UI first and only reaches asset/drum_kit/*.drumkit when you press 💾 保存鼓组配置 / 💾 Save Drum Kit Config at the bottom of the page.",
              ),
            },
            {
              kind: "ul",
              items: [
                l(
                  "成功：绿色横幅「配置已保存至: asset/drum_kit/xxx.drumkit」。",
                  "Success: the green banner reads “Config saved to: asset/drum_kit/xxx.drumkit”.",
                ),
                l(
                  "有名字带空格：横幅「保存失败：以下名称包含空格:」并逐行列出是哪个组件 / 特殊动作（组件: xxx / 特殊动作: xxx）。",
                  "A name with a space: the banner reads 保存失败：以下名称包含空格 / Save failed: the following names contain spaces: and lists the offending components / actions line by line (Component: xxx / Special Action: xxx).",
                ),
                l(
                  "写出去的字段：鼓组名、limbs、全部组件（名称 / 描述 / 可驱动肢体 / MIDI 触发器）与全部特殊动作。",
                  "What gets written: the kit name, limbs, every component (name / description / drivable limbs / MIDI triggers) and every special action.",
                ),
              ],
            },
            {
              kind: "callout",
              tone: "warn",
              title: l("两套保存不要混", "Two separate save paths"),
              text: l(
                "上面的 💾 保存 改的是 drumers.json（谁挂了哪份配置），下面的 💾 保存鼓组配置 改的是 .drumkit（鼓组里有什么）。而且改完鼓组还要回 ▶ 执行操作 重新生成一次，因为映射是生成时才读的。",
                "💾 Save up top writes drumers.json (which performer uses which profile); 💾 保存鼓组配置 / 💾 Save Drum Kit Config writes the .drumkit (what the kit contains). And after changing a kit you must regenerate on ▶ Execute, because the mapping is read at generation time.",
              ),
            },
          ],
        },
      ],
    },

    // ─────────────────────────────────────────────────────────────
    {
      id: "tab-midi",
      title: l("页签 2：MIDI 处理", "Tab 2 — MIDI Processing"),
      image: { image: 1 },
      blocks: [
        {
          kind: "p",
          text: l(
            "🎵 MIDI 处理 是左右两栏：**左栏**是「MIDI 文件管理」+「参数设置」+ 提示横幅，**右栏**是「轨道选择」。左栏决定「用哪份文件、怎么解析」，右栏决定「生成哪些轨」。",
            "🎵 MIDI Processing is two columns: the **left** holds MIDI 文件管理 / MIDI File Management, 参数设置 / Parameter Settings and the status banner; the **right** holds 轨道选择 / Track Selection. The left decides which file and how it is parsed; the right decides which tracks get animated.",
          ),
        },
        {
          kind: "p",
          text: l(
            "截图里这份 MIDI 已经有文件在用了（当前文件: 灼火之心remake.mid），轨道表列了 9 行、勾中了轨道 11（通道 9，也就是 MIDI 标准里的第 10 通道/鼓通道），左栏「已选择的轨道:」显示 11。",
            "In the screenshot a file is already loaded (当前文件 / Current file: 灼火之心remake.mid), the track table lists nine rows, track 11 is ticked (its 通道 / Channel column reads 9 — the drum channel, i.e. channel 10 in one-based terms), and 已选择的轨道 / Selected tracks: shows 11.",
          ),
        },
      ],
      subs: [
        {
          id: "midi-file",
          title: l(
            "MIDI 文件管理：加载与扫描",
            "MIDI file management: load and scan",
          ),
          blocks: [
            {
              kind: "ol",
              items: [
                l(
                  "点 📂 选择 MIDI 文件，在文件对话框里选 .mid / .midi（对话框的过滤器就叫 MIDI文件）。",
                  "Click 📂 选择 MIDI 文件 / 📂 Select MIDI File and pick a .mid / .midi file in the dialog (the filter is labelled MIDI文件 / MIDI files).",
                ),
                l(
                  "也可以直接把文件拖到卡片下方的虚线区 📥 将 MIDI 文件拖放到此处（副标题「支持 .mid 和 .midi 格式」）。",
                  "You can also drag a file onto the dashed 📥 将 MIDI 文件拖放到此处 / 📥 Drop a MIDI file here area below (subtitle “Supports .mid and .midi formats”).",
                ),
                l(
                  "选中后卡片顶部把「未选择 MIDI 文件」换成 当前文件: 加文件名（过长会截断显示）。",
                  "Once loaded, the card header swaps 未选择 MIDI 文件 / No MIDI file selected for 当前文件 / Current file: plus the file name (truncated if long).",
                ),
                l(
                  "点 🔍 扫描 MIDI 信息，软件解析这份文件并把结果填进右栏的轨道表。",
                  "Click 🔍 扫描 MIDI 信息 / 🔍 Scan MIDI Info; the app parses the file and fills the track table on the right.",
                ),
              ],
            },
            {
              kind: "table",
              head: [l("情况", "Situation"), l("横幅会写什么", "Banner text")],
              rows: [
                [
                  l("手动选了别的扩展名", "Manual pick with a wrong extension"),
                  l(
                    "错误：文件扩展名不正确，期望.mid或.midi，实际为.xxx（扩展名比较不区分大小写）",
                    "Error: incorrect file extension; expected .mid or .midi, got .xxx (the comparison ignores case)",
                  ),
                ],
                [
                  l(
                    "手动选了没有扩展名的文件",
                    "Manual pick with no extension",
                  ),
                  l(
                    "错误：选择的文件没有扩展名",
                    "Error: the selected file has no extension",
                  ),
                ],
                [
                  l("拖放了别的扩展名", "Drop with a wrong extension"),
                  l(
                    "错误：文件扩展名不正确，期望 .mid 或 .midi，实际为 .xxx（拖放是另一套文案）",
                    "Error: incorrect file extension; expected .mid or .midi, got .xxx (drag-and-drop uses its own wording)",
                  ),
                ],
                [
                  l("拖放了没有扩展名的文件", "Drop with no extension"),
                  l(
                    "错误：拖放的文件没有扩展名",
                    "Error: the dropped file has no extension",
                  ),
                ],
                [
                  l("还没选文件就点扫描", "Scan before loading a file"),
                  l(
                    "错误：请先选择MIDI文件",
                    "Error: please select a MIDI file first",
                  ),
                ],
                [
                  l("扫描时解析失败", "Parsing fails while scanning"),
                  l(
                    "MIDI文件不存在或格式错误，同时控制台打印「获取轨道信息时出错: …」",
                    "MIDI file does not exist or is in the wrong format, and the console also prints “Error getting track info: …”",
                  ),
                ],
              ],
            },
            {
              kind: "note",
              text: l(
                "左栏底部的横幅显示 3 秒后自动消失。它按文案判断配色：含「错误」「Error」「不存在」「does not exist」时是红色错误样式，其余是绿色样式。两种失败都不会替换掉当前已经加载的 MIDI 文件。",
                "The banner at the bottom of the left column disappears after three seconds. Its colour comes from the text: it turns red when the message reads 错误 / Error, or contains “Error” or “does not exist”, and green otherwise. Neither failure replaces the MIDI file you already have loaded.",
              ),
            },
          ],
        },
        {
          id: "midi-params",
          title: l(
            "参数设置：三个参数 + 已选轨道",
            "Parameter settings: three parameters plus the selection",
          ),
          blocks: [
            {
              kind: "p",
              text: l(
                "左栏第二张卡片是「参数设置」（副标题「配置 MIDI 处理参数」）。上面三个参数，下面是把当前选择列出来的「已选择的轨道:」。",
                "The second card in the left column is 参数设置 / Parameter Settings (subtitle “Configure MIDI processing parameters”): three parameters on top, the current selection as 已选择的轨道 / Selected tracks: below.",
              ),
            },
            {
              kind: "table",
              head: [
                l("参数", "Parameter"),
                l("作用", "What it does"),
                l("范围", "Range"),
                l("默认", "Default"),
                l("怎么调", "How to tune"),
              ],
              rows: [
                [
                  l("使用通道:", "Use channel:"),
                  l(
                    "只处理指定 MIDI 通道上的音符；-1 表示所有通道。软件按文件里的通道号比较，**从 0 起算**：MIDI 标准里的第 10 通道（鼓通道）在这里是 9。",
                    "Processes only notes on one MIDI channel; -1 means all channels. It compares against the channel number stored in the file, **counting from 0**: the drum channel (channel 10 in the MIDI standard) is 9 here.",
                  ),
                  l(
                    "任意整数（软件不限制范围，拖动步进 1）",
                    "Any integer (the app sets no range; dragging steps by 1)",
                  ),
                  l("-1", "-1"),
                  l(
                    "一份 MIDI 把鼓和旋律写在同一条轨的不同通道上时，用它收窄范围。拿不准就先留 -1：截图里勾中轨道 11 时，通道 列显示的就是 9。",
                    "Use it when one track mixes drums and melodic parts on different channels. If unsure, keep -1: in the screenshot, ticked track 11 shows 9 in the 通道 / Channel column.",
                  ),
                ],
                [
                  l("帧率:", "FPS:"),
                  l(
                    "动画帧率。MIDI 的 tick 会按它换算成帧，所以它决定了最终动画的时间刻度与总帧数。",
                    "Animation frame rate. MIDI ticks are converted to frames with it, so it sets the time base and the total frame count of the exported animation.",
                  ),
                  l(
                    "任意正数（拖动步进 1）",
                    "Any positive number (dragging steps by 1)",
                  ),
                  l("60", "60"),
                  l(
                    "与目标工程保持一致最省事（Blender / Unreal 常用 30 或 60）。改了帧率必须重新生成，否则新的动画长度和旧结果对不上。",
                    "Matching your target project saves trouble (30 or 60 are common in Blender / Unreal). If you change it, regenerate — otherwise the new animation length no longer lines up with old results.",
                  ),
                ],
                [
                  l("音符量化:", "Note quantization:"),
                  l(
                    "把相邻很近的音符对齐到同一格，让同一拍上的多个鼓件真正落在同一帧。",
                    "Snaps nearby notes onto the same grid so several pieces on one beat really land on the same frame.",
                  ),
                  l(
                    "无量化 / 1/32 / 1/64 / 1/128（**没有 1/16**）",
                    "无量化 / No quantization, 1/32, 1/64, 1/128 (**there is no 1/16**)",
                  ),
                  l("无量化", "无量化 / No quantization"),
                  l(
                    "真人演奏或从音频识别出来的 MIDI 常有几毫秒抖动，量化一下动作会更干净；写谱导出的规整 MIDI 保持 无量化 即可。四个选项里 1/32 最粗、1/128 最细。",
                    "Human-played or audio-detected MIDI jitters by a few milliseconds and quantizing reads cleaner; neatly sequenced MIDI can stay at 无量化 / No quantization. Of the four options 1/32 is the coarsest and 1/128 the finest.",
                  ),
                ],
                [
                  l("已选择的轨道:", "Selected tracks:"),
                  l(
                    "把当前选中的轨道号用 chip 列出来。它是**生成阶段真正使用的轨道**，和右栏的勾选框是两回事。",
                    "Lists the currently selected track numbers as chips. These are **the tracks generation actually uses** — a different thing from the checkboxes on the right.",
                  ),
                  l("—", "—"),
                  l("[0]", "[0]"),
                  l(
                    "它不会跟着右栏的勾选实时变化，只有点过 ✅ 添加选中的轨道 才更新。一个都没选时显示斜体的「无轨道」。",
                    "It does not follow the checkboxes live: it only updates after you click ✅ 添加选中的轨道 / ✅ Add Selected Tracks. With nothing selected it shows an italic 无轨道 / No tracks.",
                  ),
                ],
              ],
            },
            {
              kind: "note",
              text: l(
                "使用通道: 与 帧率: 都是可拖动的数值框（鼠标按住左右拖即可改）。这两个值的改动会立刻影响下一次生成，但不会自动重新生成。",
                "Use channel: and FPS: are draggable number fields — press and drag left or right to change them. Edits take effect on the next run but never trigger one by themselves.",
              ),
            },
          ],
        },
        {
          id: "midi-tracks",
          title: l(
            "轨道选择：决定生成哪些音",
            "Track selection: which notes get animated",
          ),
          blocks: [
            {
              kind: "p",
              text: l(
                "扫描完成后，右栏「轨道选择」（副标题「从扫描结果中选择轨道」）列出这份 MIDI 里**所有含音符的轨道**。一个音符都没有的轨不会出现在表里。表格五列：",
                "After scanning, 轨道选择 / Track Selection (subtitle “Select tracks from the scan results”) lists **every track that contains notes**. Tracks with zero notes never appear. The table has five columns:",
              ),
            },
            {
              kind: "table",
              head: [
                l("列", "Column"),
                l("含义", "Meaning"),
                l("怎么用", "How to use it"),
              ],
              rows: [
                [
                  l("选择", "Select"),
                  l(
                    "勾选框，勾中的行会在下一次点添加时被采纳。",
                    "A checkbox; ticked rows are taken when you next press Add.",
                  ),
                  l(
                    "只勾真正需要生成动画的轨道。鼓手动画通常只勾鼓轨，不要勾旋律轨或铺底。",
                    "Tick only the tracks that should be animated. For a drummer, tick the drum track, not melodic or pad tracks.",
                  ),
                ],
                [
                  l("轨道", "Track"),
                  l(
                    "轨道序号，会体现在输出文件名里（{角色名}_{MIDI名}_{轨道}）。",
                    "The track index. It shows up in the output file names ({performer}_{midi}_{tracks}).",
                  ),
                  l(
                    "记下来。后面排查 Recorder 文件不存在 时就靠它。",
                    "Note it down — you need it when debugging “Recorder file does not exist”.",
                  ),
                ],
                [
                  l("通道", "Channel"),
                  l(
                    "该轨用到的 MIDI 通道号，从 0 起算。",
                    "The MIDI channel used by the track, counting from 0.",
                  ),
                  l(
                    "配合 使用通道: 判断是否需要收窄范围。鼓轨通常是 9。",
                    "Use it with 使用通道 / Use channel: to decide whether to narrow the scope. A drum track is usually 9.",
                  ),
                ],
                [
                  l("乐器", "Instrument"),
                  l(
                    "软件按轨道里的 Program Change 事件推断出的乐器名；没有这个事件时显示 Unknown instrument（过长会截断）。",
                    "The instrument name inferred from the track's Program Change event, or Unknown instrument when there is none (truncated if long).",
                  ),
                  l(
                    "用来确认这条轨到底是不是鼓。打击乐轨常常没有 Program Change，所以显示 Unknown instrument 是正常的——这时看 通道 和 音符数 更可靠。",
                    "Confirms whether the track really is drums. Percussion tracks often carry no Program Change, so Unknown instrument is normal — lean on Channel and Notes instead.",
                  ),
                ],
                [
                  l("音符数", "Notes"),
                  l("该轨的音符总数。", "Total notes on the track."),
                  l(
                    "数量异常小通常意味着选错了轨。",
                    "An unusually small count usually means the wrong track.",
                  ),
                ],
              ],
            },
            {
              kind: "p",
              text: l(
                "这张表是按「轨道 × 通道」列出来的，所以同一条轨用了多个通道时会占好几行——截图里轨道 4/5/6/7/8/9/10/11 各占一行就是这个原因。",
                "The table is built per track-and-channel pair, so a single track that uses several channels takes several rows — that is why the screenshot shows tracks 4/5/6/7/8/9/10/11 as separate lines.",
              ),
            },
            {
              kind: "ol",
              items: [
                l(
                  "勾选目标轨道（可以多选）。重新扫描后，已经在「已选择的轨道」里的轨会默认保持勾选。",
                  "Tick the target track(s); multiple selection is fine. After a re-scan, tracks already in Selected tracks: come back pre-ticked.",
                ),
                l(
                  "点 ✅ 添加选中的轨道。它会**先清空原有选择**，再写入本次勾选结果并按轨道号从小到大排序。",
                  "Click ✅ 添加选中的轨道 / ✅ Add Selected Tracks. It **clears the previous selection first**, then writes the ticked tracks sorted ascending by index.",
                ),
                l(
                  "回左栏「参数设置」，确认 已选择的轨道: 后面的 chip 就是你要的轨道号。",
                  "Back in “Parameter Settings” on the left, confirm the chips after 已选择的轨道 / Selected tracks: are the ones you want.",
                ),
              ],
            },
            {
              kind: "callout",
              tone: "warn",
              text: l(
                "勾选本身不生效，必须点一次 ✅ 添加选中的轨道。反过来也要小心：因为它会先清空，**一个都没勾就点**的结果是把选择清成「无轨道」，执行阶段就无从下手了。",
                "Ticking alone does nothing — you must click ✅ 添加选中的轨道 / ✅ Add Selected Tracks. The flip side matters too: because it clears first, clicking it **with nothing ticked** empties the selection to 无轨道 / No tracks, leaving generation with nothing to work on.",
              ),
            },
            {
              kind: "note",
              text: l(
                "表格一直显示「请先扫描 MIDI 文件以查看轨道信息」时，要么还没点 🔍 扫描 MIDI 信息，要么这份 MIDI 里确实没有含音符的轨道（例如选错了文件）。",
                "If the table keeps showing 请先扫描 MIDI 文件以查看轨道信息 / Scan a MIDI file first to view track information, either you have not clicked 🔍 扫描 MIDI 信息 / 🔍 Scan MIDI Info yet, or the file genuinely has no track with notes (a wrong file, for instance).",
              ),
            },
          ],
        },
      ],
    },

    // ─────────────────────────────────────────────────────────────
    {
      id: "tab-execute",
      title: l("页签 3：执行操作", "Tab 3 — Execute"),
      image: { image: 2 },
      blocks: [
        {
          kind: "p",
          text: l(
            "▶ 执行操作 左栏自上而下是「角色概览」「MIDI 参数」「执行操作」三张卡片，右栏整块是「控制台输出」。左栏上面两张卡片只是**结果回显**，不能在这里改任何设置。",
            "▶ Execute stacks three cards in the left column — 角色概览 / Character Overview, MIDI 参数 / MIDI Parameters and the run buttons — with 控制台输出 / Console Output filling the right column. The top two left-hand cards are **read-only echoes**; no setting can be changed here.",
          ),
        },
        {
          kind: "note",
          text: l(
            "截图拍到的是**正在运行**的状态：按钮已经变成一个 🔄 执行中...，控制台里能看到 MIDI 统计和「正在处理第 501/589 帧」这样的进度。软件自带的两张截图分别是运行中和跑完之后的样子。",
            "The screenshot catches a **run in progress**: the buttons have collapsed into a single 🔄 执行中 / 🔄 Running... and the console is showing MIDI statistics plus progress lines like “Processing frame 501/589…”. The two bundled screenshots happen to show a running state and a finished one.",
          ),
        },
      ],
      subs: [
        {
          id: "execute-echo",
          title: l(
            "两张回显卡片：点之前先核对",
            "The two echo cards: check before you press",
          ),
          blocks: [
            {
              kind: "ul",
              items: [
                l(
                  "角色概览（副标题「当前选择的鼓手信息」）：当前鼓手的名字，以及 鼓组部件: 一行——把这份鼓组里每个组件的名字用逗号连起来显示（只读文本框）。没选角色时显示「未选择角色」，鼓组读不出来时显示「鼓组部件：未加载」。",
                  "角色概览 / Character Overview (subtitle “Currently selected drummer”): the current drummer's name plus a 鼓组部件 / Drum kit parts: line joining every component name with commas (a read-only text box). With no performer it reads 未选择角色 / No character selected; if the kit could not be read it reads 鼓组部件：未加载 / Drum kit parts: not loaded.",
                ),
                l(
                  "MIDI 参数（副标题「MIDI 文件信息」）：MIDI: 文件名 加完整路径，以及 轨道:（按数组显示，例如 [11]）、通道:、帧率:。没选文件时显示「未选择 MIDI 文件」。",
                  "MIDI 参数 / MIDI Parameters (subtitle “MIDI file info”): MIDI: file name plus the full path, and 轨道 / Track: (shown as an array, e.g. [11]), 通道 / Channel: and 帧率 / FPS:. With no file it reads 未选择 MIDI 文件 / No MIDI file selected.",
                ),
              ],
            },
            {
              kind: "callout",
              tone: "warn",
              text: l(
                "看到「未选择角色」或「未选择 MIDI 文件」时不要直接点生成——那只会往控制台写一行「错误: 未选择角色」或「错误: 未选择MIDI文件」，然后什么都不发生。回 ⚙ 角色设置 / 🎵 MIDI 处理 补齐再回来。",
                "When you see 未选择角色 / No character selected or 未选择 MIDI 文件 / No MIDI file selected, do not press generate — it only appends “Error: no character selected” or “Error: no MIDI file selected” to the console and nothing else happens. Go back to ⚙ Character Settings / 🎵 MIDI Processing, fix it, then return.",
              ),
            },
          ],
        },
        {
          id: "execute-buttons",
          title: l("两个生成按钮", "The two generate buttons"),
          blocks: [
            {
              kind: "p",
              text: l(
                "第三张卡片上只有两个按钮，跑的就是本产品的全部工作。",
                "The third card holds just two buttons, and between them they do all of this product's work.",
              ),
            },
            {
              kind: "table",
              head: [
                l("按钮", "Button"),
                l("做什么", "What it does"),
                l("什么时候用", "When to use it"),
              ],
              rows: [
                [
                  l(
                    "⚡ 一键生成（MIDI → Recorder → 动画）",
                    "⚡ Generate All (MIDI → Recorder → Animation)",
                  ),
                  l(
                    "跑完整条链路：解析 MIDI 并打印统计 → 逐帧搜索最优的肢体 ↔ 鼓件分配（这一步最耗时）→ 导出 output/recorder/….recorder → 生成 ….animation 与 …_shapekey.animation → 写出 output/final_result/….beatbloom。",
                    "Runs the whole chain: parse the MIDI and print statistics → search the optimal limb ↔ piece assignment frame by frame (the slow part) → export output/recorder/….recorder → generate ….animation and …_shapekey.animation → write output/final_result/….beatbloom.",
                  ),
                  l(
                    "第一次跑新素材；或者换了鼓手、MIDI、轨道、帧率:、音符量化: 之后。",
                    "First run on new material, or after changing drummer, MIDI, tracks, 帧率 / FPS: or 音符量化 / Note quantization:.",
                  ),
                ],
                [
                  l(
                    "🎬 生成动画（Recorder → 动画）",
                    "🎬 Generate Animation (Recorder → Animation)",
                  ),
                  l(
                    "跳过搜索，直接读已经存在的 output/recorder/{角色名}_{MIDI名}_{轨道}.recorder，只重算动画并写出 .animation / _shapekey.animation / .beatbloom。",
                    "Skips the search and reads the existing output/recorder/{performer}_{midi}_{tracks}.recorder, recomputing only the animation and writing .animation / _shapekey.animation / .beatbloom.",
                  ),
                  l(
                    "用 📝 Recorder 编辑器 手工改过分配、或只改了 Head_Control 阈值 之后。它比一键生成快得多。",
                    "After hand-editing the assignment in 📝 Recorder Editor, or after changing only the Head_Control Threshold. It is far faster than a full run.",
                  ),
                ],
              ],
            },
            {
              kind: "callout",
              tone: "info",
              title: l(
                "运行期间按钮会变，而且不能取消",
                "The buttons change, and there is no cancel",
              ),
              text: l(
                "生成期间两个按钮会被替换成一个 🔄 执行中...，点它**没有任何反应**——软件里没有取消功能。想中断只能关掉程序，此时已经写出的中间文件会留在 output/ 里。判断还在不在跑：看按钮是否变回两个，或者看日志还在不在增长。",
                "While running, both buttons are replaced by a single 🔄 执行中 / 🔄 Running... that **does nothing when clicked** — there is no cancel. The only way to interrupt is to close the app, and intermediate files already written stay in output/. To tell whether it is still working, watch for the two buttons to return or the log to keep growing.",
              ),
            },
            {
              kind: "callout",
              tone: "warn",
              title: l(
                "「仅生成动画」不会替你检查参数",
                "Animation-only does not validate your settings",
              ),
              text: l(
                "它是按「**当前**角色名 + 当前 MIDI 文件名 + 当前轨道号」拼出 recorder 路径去读文件的。换了鼓手或改了轨道勾选之后路径就变了，会出现「Recorder 文件不存在: output/recorder/…」。解决办法只有一个：回 ⚡ 一键生成 重新跑一遍。",
                "It builds the recorder path from the **current** performer name, MIDI file name and track indices. Change the performer or the track ticks and the path changes, producing “Recorder file does not exist: output/recorder/…”. The only fix is to go back and run ⚡ Generate All (MIDI → Recorder → Animation) again.",
              ),
            },
          ],
        },
        {
          id: "execute-console",
          title: l(
            "控制台输出：怎么判断跑成功了",
            "Console output: reading a run",
          ),
          blocks: [
            {
              kind: "p",
              text: l(
                "右栏「控制台输出」实时刷新全过程日志，标题右侧有三个按钮；日志为空时中间显示「暂无输出」。",
                "控制台输出 / Console Output streams the whole log, with three buttons to the right of the title; while it is empty the middle reads 暂无输出 / No output yet.",
              ),
            },
            {
              kind: "table",
              head: [l("按钮", "Button"), l("作用", "What it does")],
              rows: [
                [
                  l("📋 复制结果", "📋 Copy Result"),
                  l(
                    "在日志里**从下往上**找第一条同时含 .beatbloom 和「已生成」的记录，把冒号后面的路径复制到剪贴板。成功时追加一条「✅ 结果路径已复制到剪贴板」，找不到时追加「⚠️ 未找到可复制的结果」。",
                    "Scans the log **bottom-up** for the first line containing both .beatbloom and 已生成 / generated, and copies whatever follows the colon. On success it appends “✅ Result path copied to clipboard”; otherwise “⚠️ No result found to copy”.",
                  ),
                ],
                [
                  l("📋 复制全部", "📋 Copy All"),
                  l(
                    "把整段日志拼成文本复制到剪贴板，方便贴给作者排查。成功追加「✅ 全部信息已复制到剪贴板」，日志为空时是「⚠️ 没有可复制的内容」。",
                    "Copies the whole log as text — handy when asking the author for help. It appends “✅ All info copied to clipboard”, or “⚠️ Nothing to copy” when the log is empty.",
                  ),
                ],
                [
                  l("🚮 清空信息", "🚮 Clear"),
                  l(
                    "清空当前日志显示（不影响磁盘上的任何文件）。注意每次点生成按钮时日志也会被自动清空。",
                    "Clears the log view (it touches no files on disk). Note that the log is also cleared automatically every time you press a generate button.",
                  ),
                ],
              ],
            },
            {
              kind: "p",
              text: l(
                "日志按级别着色：绿色 = 成功、红色 = 错误、黄色 = 警告、白色等宽字体 = 普通信息。这一页没有「日志 N 条」这类计数徽标，判断进度靠两件事：日志还在不在增长，以及卡片上的按钮是否还是 🔄 执行中...。",
                "Log lines are coloured by severity: green = success, red = error, yellow = warning, white monospace = plain info. There is no “N log lines” badge here; progress shows through two things — whether the log keeps growing, and whether the button still reads 🔄 执行中 / 🔄 Running....",
              ),
            },
            {
              kind: "ul",
              items: [
                l(
                  "**开头**：开始执行... → 开始处理... → 输出路径: output/recorder/… → 加载MIDI文件...。",
                  "**Opening lines**: Starting execution... → Starting processing... → Output path: output/recorder/… → Loading MIDI file....",
                ),
                l(
                  "**MIDI 统计**：全曲每拍 tick 数、初始速度下每拍约多少帧（含 BPM 与每拍秒数）、和弦 / 单音符总数、按下的音符总数、不重复音符数，以及按当前帧率换算出的总 tick / 总帧 / 总秒数。这些数字是判断「文件选对了没有」最快的方法。",
                  "**MIDI statistics**: ticks per beat, frames per beat at the initial tempo (with BPM and seconds per beat), total chords / single notes, notes pressed, unique notes, and the total ticks / frames / seconds at the current frame rate. These numbers are the fastest way to confirm you picked the right file.",
                ),
                l(
                  "**处理阶段**：加载角色信息... / 加载鼓组信息... → 处理音符映射... → 每 100 帧一条 正在处理第 {}/{} 帧，平均速度为{}帧/秒 → 处理完成，耗时{}秒。",
                  "**Processing**: Loading character info... / Loading drum kit info... → Processing note mapping... → one Processing frame {}/{} at {} frames/s line every 100 frames → Processing complete, took {} s.",
                ),
                l(
                  "**最优方案**：已导出最佳recorder到 …，熵值: …，接着「映射的component如下:」逐条列出每个音高被哪些鼓件认领、出现多少次。",
                  "**Best solution**: Exported best recorder to {}, entropy: {}, followed by “Mapped components:” listing which pieces claim each pitch and how often.",
                ),
                l(
                  "**未映射音符**：全部覆盖时打印「所有notes都已成功映射」；否则打印「未映射音符统计:」并逐条列出 音高: 名称, 出现次数: N。",
                  "**Unmapped notes**: “All notes mapped successfully” when everything is covered; otherwise “Unmapped note statistics:” with one pitch: name, occurrences: N line each.",
                ),
                l(
                  "**收尾**：生成动画数据... → 两行 [HeadControl] … 统计（生成的关键帧数、其中多少用了位置修正、约束检查通过率）→ 生成形状键动画数据成功，保存在… → Beatbloom配置文件已生成: …（绝对路径）→ 执行完成!。",
                  "**Closing lines**: Generating animation data... → two [HeadControl] … lines (keyframes generated, how many needed position correction, constraint-check pass rate) → Shape key animation data generated, saved to … → Beatbloom config file generated: … (an absolute path) → Execution complete!.",
                ),
              ],
            },
            {
              kind: "callout",
              tone: "tip",
              title: l(
                "看到「未映射音符统计」怎么办",
                "When you see unmapped notes",
              ),
              text: l(
                "这说明这些音高没有被任何鼓件的 MIDI 触发器 认领，它们不会产生任何动作（只是被忽略，不会中断生成）。回 ⚙ 角色设置 的「鼓组信息」，给合适的鼓件 添加触发器 补上音高，保存鼓组配置后重新生成一遍。",
                "It means no piece's MIDI 触发器 / MIDI Triggers claims those pitches, so they produce no motion at all (they are ignored rather than fatal). Go to 鼓组信息 / Drum Kit Info under ⚙ 角色设置 / ⚙ Character Settings, add the pitches to the right piece with 添加触发器 / Add Trigger, save the kit, and regenerate.",
              ),
            },
          ],
        },
        {
          id: "execute-output",
          title: l("生成文件都在哪", "Where the generated files go"),
          blocks: [
            {
              kind: "p",
              text: l(
                "一轮生成会在程序自己的 output/ 目录下写出下面这些文件。文件名里的 {轨道} 多选时用下划线连接，例如 …_1_2.recorder。",
                "A run writes the following files under the app's own output/ folder. With several tracks the {tracks} part is joined by underscores, as in …_1_2.recorder.",
              ),
            },
            {
              kind: "table",
              head: [
                l("路径", "Path"),
                l("内容", "Contents"),
                l("要不要留", "Keep it?"),
              ],
              rows: [
                [
                  l(
                    "output/final_result/{角色名}_{MIDI名}_{轨道}.beatbloom",
                    "output/final_result/{performer}_{midi}_{tracks}.beatbloom",
                  ),
                  l(
                    "最终结果 JSON，只有 animation_path 与 shape_key_animation_path 两个字段，值是另外两个文件的**绝对路径**。**插件加载的就是它**。",
                    "The final result JSON with just two fields — animation_path and shape_key_animation_path — holding the **absolute paths** of the other two files. **This is what the plugin loads.**",
                  ),
                  l("必须保留", "Essential"),
                ],
                [
                  l(
                    "output/animation/{角色名}_{MIDI名}_{轨道}.animation",
                    "output/animation/{performer}_{midi}_{tracks}.animation",
                  ),
                  l(
                    "四肢与头部的关键帧动画。",
                    "The limb and head keyframe animation.",
                  ),
                  l("必须保留", "Essential"),
                ],
                [
                  l(
                    "output/animation/{角色名}_{MIDI名}_{轨道}_shapekey.animation",
                    "output/animation/{performer}_{midi}_{tracks}_shapekey.animation",
                  ),
                  l(
                    "各鼓件被击打时的形状键动画，只包含鼓组里实际存在的组件。",
                    "Shape-key animation for every struck piece, covering only the components that exist in the kit.",
                  ),
                  l("必须保留", "Essential"),
                ],
                [
                  l(
                    "output/recorder/{角色名}_{MIDI名}_{轨道}.recorder",
                    "output/recorder/{performer}_{midi}_{tracks}.recorder",
                  ),
                  l(
                    "逐帧的四肢与鼓件分配记录。📝 Recorder 编辑器 读写它，🎬 生成动画 也从它出发。",
                    "The frame-by-frame limb-to-piece assignment. 📝 Recorder Editor reads and writes it, and 🎬 Generate Animation (Recorder → Animation) starts from it.",
                  ),
                  l("建议保留", "Recommended"),
                ],
                [
                  l(
                    "output/current_midi_info.txt",
                    "output/current_midi_info.txt",
                  ),
                  l(
                    "最近一次解析 MIDI 时导出的第一条轨道原始事件文本，纯调试用，插件不读。",
                    "The raw event dump of the first track from the last parse; debugging only, and the plugin never reads it.",
                  ),
                  l("可删", "Safe to delete"),
                ],
                [
                  l(
                    "asset/temp/notes_map.json、asset/temp/messages.json 等",
                    "asset/temp/notes_map.json, asset/temp/messages.json, …",
                  ),
                  l(
                    "音符映射与消息的中间数据，**每次生成都会被覆盖重写**。",
                    "Intermediate note-map and message data. **Rewritten on every generation.**",
                  ),
                  l(
                    "可删（会自动重建）",
                    "Safe to delete (rebuilt automatically)",
                  ),
                ],
              ],
            },
            {
              kind: "callout",
              tone: "warn",
              title: l(
                "不要随手挪动输出目录",
                "Do not move the output folder casually",
              ),
              text: l(
                ".beatbloom 里记录的是另外两个文件的绝对路径。把 output/ 整体改名、搬到别的盘或复制到另一台机器，插件就会找不到数据——这时重新生成一遍即可。",
                "The .beatbloom file stores the **absolute paths** of the other two files. Rename output/, move it to another drive, or copy it to another machine and the plugin can no longer find them — just regenerate.",
              ),
            },
          ],
        },
      ],
    },

    // ─────────────────────────────────────────────────────────────
    {
      id: "tab-audio",
      title: l("页签 4：音频转MIDI", "Tab 4 — Audio to MIDI"),
      image: { image: 3 },
      blocks: [
        {
          kind: "p",
          text: l(
            "🎼 音频转MIDI（副标题「将多个 MP3 文件合并为单个打击乐轨道」）解决的是「手里只有音频、没有鼓 MIDI」的情况：把一串 MP3 / WAV 按打击乐类型标好，软件用起音检测找出每一段里的击打点，按权重随机分配到你设定的音高上，最后合并成**一条打击乐轨**的 MIDI 文件。生成出来的 MIDI 再拿回 🎵 MIDI 处理 当普通 MIDI 用。",
            "🎼 Audio to MIDI (subtitle “Merge multiple MP3 files into a single percussion track”) covers the “audio only, no drum MIDI” case: tag a set of MP3 / WAV clips by percussion type, and the app onset-detects the hits, assigns pitches randomly by weight and merges everything into a **single percussion-track** MIDI file. That MIDI then goes back into 🎵 MIDI Processing like any other file.",
          ),
        },
        {
          kind: "p",
          text: l(
            "页面布局是左侧一条固定宽度的操作栏（「文件操作」+「输出设置」），右侧是「🎵 合并文件列表」和「📝 处理进度」。截图里列表有 6 个音频素材，类型分别是 Crash / Hi-Hat / Kick / Ride / Snare / Tom，进度日志已经跑到「✨ 全部完成！」。",
            "The layout is a fixed-width control column on the left (文件操作 / File Operations + 输出设置 / Output Settings) with 🎵 合并文件列表 / 🎵 Merge File List and 📝 处理进度 / 📝 Progress on the right. The screenshot shows six audio clips typed as Crash / Hi-Hat / Kick / Ride / Snare / Tom, with the log already at “✨ All done!”.",
          ),
        },
      ],
      subs: [
        {
          id: "audio-files",
          title: l("文件操作与文件列表", "File operations and the file list"),
          blocks: [
            {
              kind: "table",
              head: [l("按钮", "Button"), l("点了会发生什么", "What it does")],
              rows: [
                [
                  l("📁 添加文件夹", "📁 Add Folder"),
                  l(
                    "选一个文件夹，把该文件夹**第一层**里所有 .mid / .midi 一次性加进列表（注意：**只扫 MIDI，不扫音频，也不进子文件夹**）。加完横幅提示「已从文件夹加载文件：路径」。",
                    "Pick a folder and the app adds every .mid / .midi file at its **top level** in one go (note: **MIDI only, no audio, and it does not descend into subfolders**). It then reports “Files loaded from folder: path”.",
                  ),
                ],
                [
                  l("📄 添加文件", "📄 Add File"),
                  l(
                    "多选添加文件，横幅提示「已加载 N 个文件」。文件对话框的过滤器只列 mp3 与 wav，所以想加 .mid 请改用 📁 添加文件夹。",
                    "Add several files at once, reporting “Loaded N files”. The dialog filter lists only mp3 and wav, so use 📁 添加文件夹 / 📁 Add Folder to add .mid files.",
                  ),
                ],
                [
                  l("🗑️ 清空列表", "🗑️ Clear List"),
                  l(
                    "清空文件列表并提示「已清空 MIDI 列表」。",
                    "Clears the file list and reports “MIDI list cleared”.",
                  ),
                ],
              ],
            },
            {
              kind: "p",
              text: l(
                "右栏「🎵 合并文件列表」每一行从左到右是：展开箭头、来源图标、文件名、打击乐类型下拉菜单、❌。列表下方跟着一行「文件数量：N」和一条状态消息。",
                "Each row of 🎵 合并文件列表 / 🎵 Merge File List reads, left to right: expander arrow, source icon, file name, percussion-type dropdown, ❌. Below the list sit 文件数量 / Files: N and a status message.",
              ),
            },
            {
              kind: "ul",
              items: [
                l(
                  "**展开箭头**只有音频文件（.mp3 / .wav）才有，展开后能看到 🎶 音高映射。MIDI 行没有箭头。",
                  "**The expander** only exists on audio files (.mp3 / .wav); expanding it reveals 🎶 音高映射 / 🎶 Pitch Mapping. MIDI rows have no arrow.",
                ),
                l(
                  "**来源图标**：🎵 = 按音频处理（会走起音检测），🎹 = 按 MIDI 文件直接读取。",
                  "**The source icon**: 🎵 = treated as audio (goes through onset detection), 🎹 = read directly as MIDI.",
                ),
                l(
                  "**❌** 把这一行从列表里移除（不影响磁盘上的原文件）。",
                  "**❌** removes the row from the list; the file on disk is untouched.",
                ),
                l(
                  "**文件名**过长会截断显示，鼠标悬停仍能看到完整路径。",
                  "**Long file names** are truncated in the row, while the full path is still available on hover.",
                ),
              ],
            },
            {
              kind: "callout",
              tone: "info",
              title: l("文件类型是猜出来的", "The percussion type is guessed"),
              text: l(
                "每个文件刚加进来时，软件按文件名里的关键词自动猜一个打击乐类型（例如 xxx_drums_kick.wav 就是 Kick），猜不出来时默认 Kick。所以加完素材请逐个核对这一列——它决定了默认音高。",
                "When a file is added, the app guesses a percussion type from keywords in the file name (say xxx_drums_kick.wav becomes Kick), falling back to Kick when nothing matches. So check this column for every clip: it drives the default pitches.",
              ),
            },
          ],
        },
        {
          id: "audio-pitch",
          title: l("打击乐类型与音高映射", "Percussion type and pitch mapping"),
          blocks: [
            {
              kind: "p",
              text: l(
                "下拉菜单里有六个类型。切换类型会**重置该文件的音高映射**为该类型的默认值，并提示「已更改 xxx 的类型为 Xxx」。这个类型只影响统计报告里显示的乐器名与默认音高，不改变源文件。",
                "The dropdown offers six types. Switching **resets that file's pitch mapping** to the type's defaults and reports “Changed type of xxx to Xxx”. The type affects only the instrument name in the statistics and the default pitches — never the source file.",
              ),
            },
            {
              kind: "table",
              head: [
                l("类型", "Type"),
                l("文件名里认哪些关键词", "Recognised keywords"),
                l("默认音高", "Default pitches"),
              ],
              rows: [
                [
                  l("Kick", "Kick"),
                  l("kick", "kick"),
                  l("36（底鼓）", "36 (kick)"),
                ],
                [
                  l("Snare", "Snare"),
                  l("snare", "snare"),
                  l("38（军鼓）", "38 (snare)"),
                ],
                [
                  l("Hi-Hat", "Hi-Hat"),
                  l("_hh / hihat / hi_hat", "_hh / hihat / hi_hat"),
                  l("42（闭合踩镲）", "42 (closed hi-hat)"),
                ],
                [
                  l("Crash", "Crash"),
                  l("crash", "crash"),
                  l("49（Crash Cymbal 1）", "49 (Crash Cymbal 1)"),
                ],
                [
                  l("Ride", "Ride"),
                  l("ride", "ride"),
                  l("51（Ride Cymbal 1）", "51 (Ride Cymbal 1)"),
                ],
                [
                  l("Tom", "Tom"),
                  l("_toms / _tom / tom", "_toms / _tom / tom"),
                  l(
                    "45 / 47 / 48（三个嗵鼓，权重相同）",
                    "45 / 47 / 48 (three toms, equal weight)",
                  ),
                ],
              ],
            },
            {
              kind: "note",
              text: l(
                "判定是单纯的字符串包含匹配，而且按 Crash → Hi-Hat → Kick → Ride → Snare → Tom 的顺序取第一个命中的，所以文件名里同时出现多个关键词时以靠前的为准。切换类型时横幅里的类型名用的是软件内部名（例如 HiHat），和下拉菜单里显示的 Hi-Hat 不完全一样。",
                "Matching is plain substring search and takes the first hit in the order Crash → Hi-Hat → Kick → Ride → Snare → Tom, so a file name containing several keywords resolves to the earliest one. The type name in the banner uses the app's internal name (say HiHat), which does not always match the Hi-Hat shown in the dropdown.",
              ),
            },
            {
              kind: "p",
              text: l(
                "音频行展开后是 🎶 音高映射（提示「按权重随机分配音高」）：一条素材可以对应多个音高，软件**按权重随机**挑一个，所以同一类素材不会永远落在同一个鼓件上。",
                "Expanding an audio row reveals 🎶 音高映射 / 🎶 Pitch Mapping (“pitches assigned randomly by weight”): one clip may map to several pitches and the app **picks one at random by weight**, so the same kind of clip does not always land on the same piece.",
              ),
            },
            {
              kind: "table",
              head: [
                l("参数", "Parameter"),
                l("作用", "What it does"),
                l("范围", "Range"),
                l("默认", "Default"),
                l("怎么调", "How to tune"),
              ],
              rows: [
                [
                  l("音符", "Note"),
                  l(
                    "这一条映射的目标音高。",
                    "The target pitch of this mapping entry.",
                  ),
                  l("0 – 127，步进 1", "0 – 127, step 1"),
                  l("该类型的默认音高", "The type's default pitch"),
                  l(
                    "想固定落到某个鼓件就填那个鼓件的音高；音高表在顶栏 帮助 → 打击乐音高表 里。",
                    "To pin a clip to a specific piece, enter that piece's pitch; the table lives under 帮助 / Help → 打击乐音高表 / Drum Pitch Table.",
                  ),
                ],
                [
                  l("权重", "Weight"),
                  l(
                    "这条音高被抽中的相对概率。",
                    "The relative chance of this pitch being picked.",
                  ),
                  l("0.1 – 10.0，步进 0.1", "0.1 – 10.0, step 0.1"),
                  l("1.0", "1.0"),
                  l(
                    "想让某个音高更容易出现就调大。Tom 默认三条（45 / 47 / 48）权重相同，所以三个嗵鼓的出现频率大致一样；想让某个嗵鼓更多就把它调高。",
                    "Raise it to make a pitch more likely. Tom ships with three entries (45 / 47 / 48) at equal weight, so the three toms come up about equally often; raise one to favour it.",
                  ),
                ],
                [
                  l("🗑", "🗑"),
                  l(
                    "删掉这一条映射（只剩一条时不显示）。",
                    "Removes this mapping entry (hidden when only one remains).",
                  ),
                  l("—", "—"),
                  l("—", "—"),
                  l(
                    "一条素材只留一个音高，它就每次都打在同一个鼓件上。",
                    "Leave a clip with a single pitch and it always hits the same piece.",
                  ),
                ],
                [
                  l("+ 添加音高", "+ Add Pitch"),
                  l(
                    "在映射列表末尾追加一条。",
                    "Appends one entry to the mapping list.",
                  ),
                  l("—", "—"),
                  l(
                    "新增条目为「音符 36 / 权重 1.0」",
                    "A new entry is note 36 / weight 1.0",
                  ),
                  l(
                    "一条素材想覆盖多个鼓件（比如军鼓正文 + 边击）时用。",
                    "Use it when one clip should cover several pieces (a snare head plus a rimshot, say).",
                  ),
                ],
              ],
            },
            {
              kind: "note",
              text: l(
                "音高映射只对音频素材有效；MIDI 素材的音高直接来自文件本身，改这里是没用的。",
                "Pitch mapping only applies to audio clips; a MIDI clip's pitches come straight from the file, so editing this has no effect on it.",
              ),
            },
          ],
        },
        {
          id: "audio-settings",
          title: l(
            "输出设置与开始合并",
            "Output settings and starting the merge",
          ),
          blocks: [
            {
              kind: "table",
              head: [
                l("参数", "Parameter"),
                l("作用", "What it does"),
                l("范围", "Range"),
                l("默认", "Default"),
                l("怎么调", "How to tune"),
              ],
              rows: [
                [
                  l(
                    "🔇 噪声过滤（滑杆标签「比例」）",
                    "🔇 Noise Filter (slider label ratio)",
                  ),
                  l(
                    "起音检测的噪声基准系数：基准 = 平均 RMS × 这个比例。它决定多弱的声音被当成底噪忽略掉。",
                    "Noise floor factor for onset detection: floor = average RMS × this ratio. It decides how quiet a sound must be to count as noise.",
                  ),
                  l("0.0 – 1.0，步进 0.05", "0.0 – 1.0, step 0.05"),
                  l("0.30", "0.30"),
                  l(
                    "调大 = 更多弱击被当成噪声滤掉。底噪大、误检出一堆假鼓点时往上调。",
                    "Higher filters out more weak hits. Raise it when the noise floor is high and you get a swarm of false hits.",
                  ),
                ],
                [
                  l(
                    "🎚 灵敏度（滑杆标签「倍数」）",
                    "🎚 Sensitivity (slider label x)",
                  ),
                  l(
                    "起音阈值倍数：能量超过平均值多少倍才算一次击打。",
                    "Onset threshold multiplier: how far above average the energy must be to count as a hit.",
                  ),
                  l("1.0 – 8.0，步进 0.1", "1.0 – 8.0, step 0.1"),
                  l("1.50", "1.50"),
                  l(
                    "调大 = 更严格，只保留明显用力的一击；漏检太多（该有的鼓点没出来）时往下调。这两个滑杆通常一起试：先降 🔇 噪声过滤 再加 🎚 灵敏度。",
                    "Higher is stricter and keeps only obvious strikes; lower it when hits are being missed. The two sliders are usually tuned together: drop 🔇 噪声过滤 / 🔇 Noise Filter first, then raise 🎚 灵敏度 / 🎚 Sensitivity.",
                  ),
                ],
                [
                  l(
                    "📂 选择输出路径 + 下方输入框",
                    "📂 Select Output Path + the field below",
                  ),
                  l(
                    "指定输出的 .mid 路径。也可以直接在输入框里手打（占位文字「手动输入路径...」）；选定后下面用 📍 显示文件名。",
                    "Sets the output .mid path. You can also type it into the field (placeholder 手动输入路径 / Type a path manually...); once set, the file name shows next to 📍 below.",
                  ),
                  l("任意 .mid 路径", "Any .mid path"),
                  l("未设置", "Unset"),
                  l(
                    "没设路径不能开始合并。输出路径输入框清空后不会写回配置（旧路径仍然留着），想换路径请用 📂 选择输出路径 或重新输入完整路径。",
                    "Without a path you cannot merge. Clearing the field does not clear the stored path (the old one stays), so use 📂 选择输出路径 / 📂 Select Output Path or type a full path to change it.",
                  ),
                ],
                [
                  l("⚡ 开始合并", "⚡ Start Merge"),
                  l(
                    "开始处理。缺文件时报「错误：请先添加 MIDI 文件或音频文件」，缺输出路径时报「错误：请选择输出路径」。处理期间按钮变成不可点的 ⏳ 处理中...。",
                    "Starts processing. With no files it reports “Error: add MIDI or audio files first”; with no output path, “Error: select an output path”. While working the button becomes the inert ⏳ 处理中 / ⏳ Processing....",
                  ),
                  l("—", "—"),
                  l("—", "—"),
                  l(
                    "一次跑完列表里的所有行，不需要逐个转换。",
                    "It processes every row in the list in one pass — no need to convert clip by clip.",
                  ),
                ],
              ],
            },
          ],
        },
        {
          id: "audio-progress",
          title: l("处理进度与输出文件", "Progress and output files"),
          blocks: [
            {
              kind: "p",
              text: l(
                "点 ⚡ 开始合并 之后，右栏「📝 处理进度」滚动输出日志（处理中会转圈），随时可以用 清空 清掉日志显示。日志内容大致是：",
                "After you press ⚡ 开始合并 / ⚡ Start Merge, 📝 处理进度 / 📝 Progress streams the log (with a spinner while it works) and can be cleared at any time with 清空 / Clear. The log runs roughly like this:",
              ),
            },
            {
              kind: "ul",
              items: [
                l(
                  "开头一条 📊 开始处理 N 个文件...。",
                  "An opening 📊 Processing N files... line.",
                ),
                l(
                  "逐个文件：音频是 音频文件已转换：xxx，生成 N 个音符（耗时…）；MIDI 是 MIDI 文件已加载：xxx，N 个音符（耗时…）。",
                  "One line per file: for audio, Audio converted: xxx, N notes generated (took …); for MIDI, MIDI loaded: xxx, N notes (took …).",
                ),
                l(
                  "🔀 已合并 N 个音符，然后 💾 已保存合并版到：… 与 💾 已保存多轨版到：…（导出耗时…）。",
                  "🔀 Merged N notes, then 💾 Saved merged version to: … and 💾 Saved multi-track version to: … (export took …).",
                ),
                l(
                  "一份「📋 ===== 轨道统计报告 =====」，逐轨列出    轨道 N: 底鼓 (Kick Drum) - M 个音符，乐器名用的是六种打击乐类型的中文名。",
                  "A 📋 ===== Track Statistics ===== block listing Track N: Kick Drum - M notes per track; the instrument names are the localised percussion types.",
                ),
                l(
                  "收尾是 ⏱️ 全部处理耗时：… 与 ✨ 全部完成！。失败时是 ❌ 处理失败：…。",
                  "It closes with ⏱️ Total processing time: … and ✨ All done!. On failure you get ❌ Processing failed: ….",
                ),
              ],
            },
            {
              kind: "callout",
              tone: "info",
              title: l("每次会输出两个文件", "Two files come out of every run"),
              text: l(
                "除了你指定的路径（合并成单轨，使用 MIDI 通道 10），同目录还会多出一个同名加 _multitrack 后缀的文件（每个素材一条轨）。想做「一个鼓手打全套」就用合并版；想分轨调试、或者回到 🎵 MIDI 处理 里分别勾选，就用多轨版。",
                "Besides the path you chose (merged into a single track on MIDI channel 10), the same folder gets a sibling named <name>_multitrack.mid with one track per clip. Use the merged version for “one drummer plays the whole kit”; use the multi-track version to debug per clip or to tick tracks separately back in 🎵 MIDI Processing.",
              ),
            },
            {
              kind: "callout",
              tone: "warn",
              title: l("两个真实的坑", "Two real gotchas"),
              text: l(
                "一，📄 添加文件 的对话框只列 mp3 / wav，想加 .mid 得用 📁 添加文件夹。二，软件按扩展名字符串判断源类型，大写 .MP3 / .WAV 会被当成 MIDI 文件去读、然后报「加载 MIDI 文件 … 时出错」，所以音频素材请统一用小写扩展名。",
                "First, the 📄 添加文件 / 📄 Add File dialog lists only mp3 / wav — use 📁 添加文件夹 / 📁 Add Folder for .mid. Second, the source type is decided by extension string, so .MP3 / .WAV in capitals is read as a MIDI file and fails with “Error loading MIDI file …”. Keep audio extensions lowercase.",
              ),
            },
            {
              kind: "note",
              text: l(
                "从音频转出来的临时 MIDI 放在系统临时目录（名为 {素材名}_temp.mid），处理结束会打印 🧹 已清理临时文件。中途报 ❌ 处理失败：… 时这些临时文件会留在临时目录里，需要手工清理。",
                "The temporary MIDI produced from audio lives in the system temp folder ({clip}_temp.mid) and is removed at the end, reported as 🧹 Temporary files cleaned. If the run fails with ❌ Processing failed: …, those temp files stay behind and need manual cleanup.",
              ),
            },
          ],
        },
      ],
    },

    // ─────────────────────────────────────────────────────────────
    {
      id: "tab-recorder",
      title: l("页签 5：Recorder 编辑器", "Tab 5 — Recorder Editor"),
      image: { image: 4 },
      blocks: [
        {
          kind: "p",
          text: l(
            "📝 Recorder 编辑器（副标题「手动调整肢体与组件的映射关系」）用来手工修正「**哪一帧、哪条肢体、打哪个鼓件**」。软件自动搜索出来的方案整体上是最优的，但个别帧仍可能出现「该用左手却用了右手」这类不自然的分配，这一页就是留给这种时刻的。",
            "📝 Recorder 编辑器 / 📝 Recorder Editor (subtitle “Manually adjust limb-component mappings”) hand-fixes **which limb hits which piece on which frame**. The automatic search is globally optimal, yet individual frames can still read unnaturally — the right hand where the left belongs — and this tab exists for exactly that.",
          ),
        },
        {
          kind: "note",
          text: l(
            "截图里搜的是第 2000 帧，列表里出现了一条 1994.25 帧的记录（当前归属右脚、鼓件 Right_Kick_drum），说明搜索是按「以该帧为中心 ±10 帧」取的。",
            "The screenshot searches frame 2000 and the list shows one hit at frame 1994.25 (currently on the right foot, piece Right_Kick_drum) — the search takes everything within ±10 frames of the number you typed.",
          ),
        },
      ],
      subs: [
        {
          id: "recorder-prereq",
          title: l("它读的是哪个文件", "Which file it reads"),
          blocks: [
            {
              kind: "p",
              text: l(
                "文件来源是固定的：output/recorder/{角色名}_{MIDI名}_{轨道}.recorder。所以**必须先跑过一次生成**，这一页才有东西可看。",
                "The source file is fixed: output/recorder/{performer}_{midi}_{tracks}.recorder. You must have run generation at least once for this page to have anything to show.",
              ),
            },
            {
              kind: "ul",
              items: [
                l(
                  "没选鼓手时整页只显示一句「请先在角色设置面板选择鼓手」；没选 MIDI 时显示「请先在 MIDI 处理面板选择 MIDI 文件」。",
                  "With no performer the page shows only 请先在角色设置面板选择鼓手 / Select a drummer in the Character Settings panel first; with no MIDI it shows 请先在 MIDI 处理面板选择 MIDI 文件 / Select a MIDI file in the MIDI Processing panel first.",
                ),
                l(
                  "文件不存在或读不出来时显示「加载 recorder 失败: 原因」。多数情况是鼓手 / MIDI / 轨道在生成之后被改过。",
                  "If the file is missing or unreadable it shows 加载 recorder 失败 / Failed to load recorder: reason. Usually the performer, MIDI or track selection changed after the last generation.",
                ),
                l(
                  "文件读一次就缓存在内存里，🔄 刷新 才会丢弃缓存、下次从磁盘重新读。",
                  "The file is cached in memory after the first read; only 🔄 刷新 / 🔄 Refresh drops the cache so the next read comes from disk.",
                ),
              ],
            },
            {
              kind: "callout",
              tone: "warn",
              title: l(
                "它是就地覆盖，没有撤销",
                "It overwrites in place, with no undo",
              ),
              text: l(
                "确认修改并覆盖 直接改磁盘上的那份 .recorder：既没有撤销，也没有另存。改坏了只能重新跑一次 ⚡ 一键生成（MIDI → Recorder → 动画），让软件重新搜索一份最优方案覆盖回去。",
                "确认修改并覆盖 / Apply & Overwrite edits that .recorder on disk directly: there is no undo and no save-as. If you break it, the only way back is another ⚡ Generate All (MIDI → Recorder → Animation) run, which searches a fresh optimal solution and overwrites it.",
              ),
            },
          ],
        },
        {
          id: "recorder-search",
          title: l(
            "搜索帧数：找到要改的那几帧",
            "Search frame: find the frames to fix",
          ),
          blocks: [
            {
              kind: "table",
              head: [l("控件", "Control"), l("作用", "What it does")],
              rows: [
                [
                  l("搜索帧数:", "Search frame:"),
                  l(
                    "输入一个帧号。注意**输入框内容一变化就会清空当前结果列表**，这是有意为之——避免你对着旧结果改。",
                    "Type a frame number. Note that **editing the field clears the current result list** — deliberately, so you never edit against stale results.",
                  ),
                ],
                [
                  l("搜索", "Search"),
                  l(
                    "以该帧为中心，把 ±10 帧范围内、四条肢体上的所有事件取出来排成一列。列表为空时页面显示提示「输入帧数并点击搜索以查看附近的事件」。",
                    "Takes every event on all four limbs within ±10 frames of that number and lists them. While the list is empty the page shows 输入帧数并点击搜索以查看附近的事件 / Enter a frame number and click Search to view nearby events.",
                  ),
                ],
                [
                  l("🔄 刷新", "🔄 Refresh"),
                  l(
                    "丢弃缓存，下次从磁盘重新读 .recorder；同时会清空搜索帧与结果列表。控制台打印「已清除缓存，下次将重新加载文件」。",
                    "Drops the cache so the .recorder is re-read from disk next time; it also clears the search frame and the result list. The console prints “Cache cleared; file will be reloaded”.",
                  ),
                ],
              ],
            },
            {
              kind: "note",
              text: l(
                "帧号就是动画的时间刻度（按生成时的 帧率: 换算），所以在 Blender / Unreal 里看到某一刻不对时，把播放器时间码乘以帧率就能得到帧号。",
                "The frame number is the animation's time base (converted with the 帧率 / FPS: used at generation time), so when a moment looks wrong in Blender / Unreal, multiply the player's timecode by the frame rate to get it.",
              ),
            },
          ],
        },
        {
          id: "recorder-edit",
          title: l(
            "结果列表：换鼓件 / 换肢体",
            "Result list: change the piece or the limb",
          ),
          blocks: [
            {
              kind: "p",
              text: l(
                "搜索出来的每一行就是「某一帧上某条肢体做的一个动作」。一行里有四项：",
                "Each result row is one action performed by one limb on one frame. A row has four parts:",
              ),
            },
            {
              kind: "table",
              head: [l("控件", "Control"), l("含义", "Meaning")],
              rows: [
                [
                  l("帧号", "Frame number"),
                  l(
                    "保留两位小数的帧位置。",
                    "The frame position, with two decimals.",
                  ),
                ],
                [
                  l(
                    "当前: 左手 / 右手 / 左脚 / 右脚",
                    "Current: Left Hand / Right Hand / Left Foot / Right Foot",
                  ),
                  l(
                    "这条记录当前归属的肢体。",
                    "The limb this record currently belongs to.",
                  ),
                ],
                [
                  l("组件下拉菜单", "Component dropdown"),
                  l(
                    "该肢体**能驱动**的鼓件列表（来自每个鼓件的 可驱动肢体）。改它就是换这个动作打哪个鼓。",
                    "The pieces that limb **can drive** (from each piece's 可驱动肢体 / Drivable Limbs). Changing it swaps which drum the action strikes.",
                  ),
                ],
                [
                  l("肢体下拉菜单", "Limb dropdown"),
                  l(
                    "能驱动**当前所选鼓件**的肢体列表。改它就是把这个动作转移到另一条肢体上（例如从左手交给右手）。",
                    "The limbs that can drive the **currently selected piece**. Changing it moves the action to another limb (left hand to right hand, say).",
                  ),
                ],
              ],
            },
            {
              kind: "note",
              text: l(
                "两个下拉是联动的：换了组件之后，右边的肢体下拉会立刻收窄成「能打这个新鼓件」的那几条。所以顺序是——先定鼓件，再定肢体。",
                "The two dropdowns are linked: after you change the component, the limb dropdown narrows to the limbs that can hit the new piece. So the order is: pick the piece first, then the limb.",
              ),
            },
          ],
        },
        {
          id: "recorder-apply",
          title: l("确认修改并覆盖", "Apply & Overwrite"),
          blocks: [
            {
              kind: "p",
              text: l(
                "红色按钮 确认修改并覆盖 把列表里显示的每一条改动写回**同一个** .recorder 文件。控制台会打印：",
                "The red 确认修改并覆盖 / Apply & Overwrite button writes every change shown in the list back into the **same** .recorder file. The console prints:",
              ),
            },
            {
              kind: "ul",
              items: [
                l(
                  "========== Recorder 修改开始 ==========。",
                  "========== Recorder Modification Start ==========.",
                ),
                l(
                  "逐条改动：换了肢体是   帧 X: 肢体 左手 -> 右手, 组件=xxx，只换组件是   帧 X: 肢体=左手, 组件=xxx。",
                  "One line per change: Frame X: limb Left Hand -> Right Hand, component=xxx when the limb moved, or Frame X: limb=Left Hand, component=xxx when only the piece changed.",
                ),
                l(
                  "✅ 成功修改 N 个动作，文件已覆盖保存 与 ========== Recorder 修改结束 ==========。",
                  "✅ Modified N actions; file overwritten and ========== Recorder Modification End ==========.",
                ),
              ],
            },
            {
              kind: "callout",
              tone: "warn",
              text: l(
                "留意成功提示里的 N：它会把列表里显示的**每一条**记录都重新写一遍，而不只是你手动动过的那几条。",
                "Watch the N in the success message: it re-writes **every** record shown in the list, not just the rows you touched.",
              ),
            },
            {
              kind: "ol",
              items: [
                l(
                  "先在 ▶ 执行操作 跑一次 ⚡ 一键生成（MIDI → Recorder → 动画），确认对应的 .recorder 已经生成。",
                  "First run ⚡ Generate All (MIDI → Recorder → Animation) on ▶ Execute so the matching .recorder exists.",
                ),
                l(
                  "在动画里找到动作不对的时刻，记下帧号。",
                  "Find the moment that looks wrong in the animation and note its frame number.",
                ),
                l(
                  "回到 📝 Recorder 编辑器，把帧号填进 搜索帧数:，点 搜索。",
                  "Back on 📝 Recorder Editor, type that frame into Search frame: and click 搜索 / Search.",
                ),
                l(
                  "在列表里按需要改：换「组件」是换鼓件，换「肢体」是换手 / 换脚。",
                  "Edit the rows: the component dropdown changes the piece, the limb dropdown changes the hand or foot.",
                ),
                l(
                  "点 确认修改并覆盖，控制台确认出现 ✅ 成功修改 N 个动作，文件已覆盖保存。",
                  "Click 确认修改并覆盖 / Apply & Overwrite and confirm the console reports ✅ Modified N actions; file overwritten.",
                ),
                l(
                  "回 ▶ 执行操作 点 🎬 生成动画（Recorder → 动画） 重算——它读的正是你刚覆盖的那份 .recorder。",
                  "Return to ▶ Execute and click 🎬 Generate Animation (Recorder → Animation) — it reads precisely the .recorder you just overwrote.",
                ),
              ],
            },
            {
              kind: "callout",
              tone: "tip",
              title: l("典型用法", "Typical use"),
              text: l(
                "某一段连续击打里出现了明显不合理的「同一只手连打两次」或交叉绕手，先把帧号前后的十几帧一次搜出来，把该换手的几条改掉，再只重跑动画（很快），比整首重算省事得多。",
                "When a passage shows an implausible “same hand twice in a row” or a tangled crossing, search the dozen or so frames around it at once, fix the rows that should switch hands, and re-run animation-only (which is quick) instead of recomputing the whole piece.",
              ),
            },
          ],
        },
      ],
    },

    // ─────────────────────────────────────────────────────────────
    {
      id: "defaults",
      title: l("什么都不用改的对照表", "The “change nothing” table"),
      blocks: [
        {
          kind: "p",
          text: l(
            "第一次用，下面这些全都可以保持默认。右列是「你什么时候会想动它」，配合前面各节的说明看。",
            "On a first run every row below can stay at its default. The right column says when you would want to change it; the sections above explain how.",
          ),
        },
        {
          kind: "table",
          head: [
            l("参数", "Parameter"),
            l("默认值", "Default"),
            l("什么时候需要动它", "When to change it"),
          ],
          rows: [
            [
              l("Head_Control 阈值", "Head_Control Threshold"),
              l("100%", "100%"),
              l(
                "头部动作太活跃，或结果文件太大时往下调。",
                "When the head moves too busily, or the result file is bigger than you want.",
              ),
            ],
            [
              l("帧率:", "FPS:"),
              l("60", "60"),
              l(
                "目标工程不是 60 fps 时（Blender / Unreal 常用 30 或 60），改完要重新生成。",
                "When the target project is not 60 fps (30 and 60 are common in Blender / Unreal) — regenerate afterwards.",
              ),
            ],
            [
              l("使用通道:", "Use channel:"),
              l("-1", "-1"),
              l(
                "一条轨上混了多个通道、只想取其中一条时。鼓通道是 9。",
                "When one track mixes several channels and you only want one. The drum channel is 9.",
              ),
            ],
            [
              l("音符量化:", "Note quantization:"),
              l("无量化", "无量化 / No quantization"),
              l(
                "MIDI 是真人演奏或从音频识别出来的、同拍音符落点参差时，试 1/32。",
                "When the MIDI was played by a human or detected from audio and notes on one beat are scattered — try 1/32.",
              ),
            ],
            [
              l("已选择的轨道", "Selected tracks"),
              l("[0]", "[0]"),
              l(
                "每次换 MIDI 之后都要重新勾选，并点 ✅ 添加选中的轨道。",
                "After every new MIDI file: tick the tracks and press ✅ 添加选中的轨道 / ✅ Add Selected Tracks.",
              ),
            ],
            [
              l(
                "鼓组的 MIDI 触发器 / 可驱动肢体",
                "A kit's MIDI Triggers / Drivable Limbs",
              ),
              l("由自带鼓组提供", "Provided by the bundled kit"),
              l(
                "控制台出现「未映射音符统计」，或某个鼓件老是被不合适的手打到时。",
                "When the console reports 未映射音符统计 / Unmapped note statistics:, or a piece keeps being hit by the wrong limb.",
              ),
            ],
            [
              l("🔇 噪声过滤 / 🎚 灵敏度", "🔇 Noise Filter / 🎚 Sensitivity"),
              l("0.30 / 1.50", "0.30 / 1.50"),
              l(
                "音频转 MIDI 漏检或误检时，先降噪声过滤再加灵敏度。",
                "When Audio to MIDI misses hits or invents them: lower the noise filter first, then raise sensitivity.",
              ),
            ],
            [
              l("音频行的 音符 / 权重", "A clip's Note / Weight"),
              l("该类型的默认音高 / 1.0", "The type's default pitch / 1.0"),
              l(
                "想让同一类素材落在不同鼓件上时（例如军鼓 + 边击）。",
                "When one kind of clip should land on several pieces (a snare head plus a rimshot, say).",
              ),
            ],
            [
              l("搜索帧数:", "Search frame:"),
              l("空", "Empty"),
              l(
                "有具体某一帧的动作要改时，填帧号再点 搜索。",
                "When one specific frame needs fixing: type the frame number and press 搜索 / Search.",
              ),
            ],
          ],
        },
      ],
    },

    // ─────────────────────────────────────────────────────────────
    {
      id: "faq",
      title: l("参数相关常见疑问", "Parameter FAQ"),
      blocks: [
        {
          kind: "p",
          text: l(
            "**改了参数为什么动画没变？** 主流程的参数（鼓组映射、帧率:、音符量化:）都是生成时才读的：改完必须重新生成。只改了 Head_Control 阈值 时可以只点 🎬 生成动画（Recorder → 动画）；改了鼓组、轨道、帧率、量化就必须走 ⚡ 一键生成（MIDI → Recorder → 动画）。",
            "**I changed a parameter and the animation did not change.** The main-flow settings (kit mapping, 帧率 / FPS:, 音符量化 / Note quantization:) are read at generation time, so you must regenerate. If you only changed the Head_Control Threshold, 🎬 Generate Animation (Recorder → Animation) is enough; for kit, track, FPS or quantization changes you need a full ⚡ Generate All (MIDI → Recorder → Animation) run.",
          ),
        },
        {
          kind: "p",
          text: l(
            "**使用通道: 里填 9 就是第 10 通道吗？** 是。软件按文件里存的通道号比较，从 0 起算，所以 MIDI 标准里的第 10 通道（鼓通道）在这里是 9。",
            "**Does Use channel: = 9 mean channel 10?** Yes. The comparison uses the channel number stored in the file, counting from 0, so the MIDI standard's channel 10 (the drum channel) is 9 here.",
          ),
        },
        {
          kind: "p",
          text: l(
            "**为什么轨道表里同一条轨出现了好几次？** 因为这张表是按「轨道 × 通道」列出来的：一条轨用了多个通道就会占多行。勾选时看清 通道 列，别把旋律通道也勾进去。",
            "**Why does the same track appear several times?** Because the table is built per track-and-channel pair: a track using several channels takes several rows. When ticking, read the 通道 / Channel column so you do not tick a melodic channel by accident.",
          ),
        },
        {
          kind: "p",
          text: l(
            "**乐器 显示 Unknown instrument 是不是选错了轨？** 不一定。打击乐轨常常没有 Program Change 事件，所以显示 Unknown instrument 很正常；看 通道（鼓轨通常是 9）和 音符数 更可靠。",
            "**Does Unknown instrument in the 乐器 / Instrument column mean I picked the wrong track?** Not necessarily. Percussion tracks often carry no Program Change event, so Unknown instrument is normal; the 通道 / Channel column (a drum track is usually 9) and Notes are more reliable.",
          ),
        },
        {
          kind: "p",
          text: l(
            "**把鼓件的系数调到 2.0 会禁用这条肢体吗？** 不会。系数只是惩罚乘数，调大只会让求解器更不愿意用这条肢体打这个鼓件。真要禁用，请在 可驱动肢体 里用 × 把那一行删掉。",
            "**Does setting a piece's coefficient to 2.0 disable that limb?** No. The coefficient is only a penalty multiplier — raising it makes the solver less willing to use that limb, nothing more. To really disable it, remove the row with × under 可驱动肢体 / Drivable Limbs.",
          ),
        },
        {
          kind: "p",
          text: l(
            "**「特殊动作」填了为什么结果没变化？** 生成阶段只读「鼓组组件」的 MIDI 触发器 与 可驱动肢体；「特殊动作」目前只保存进 .drumkit，不参与生成。补音高、调肢体请在「鼓组组件」里做。",
            "**I filled in a special action but nothing changed.** Generation only reads the MIDI 触发器 / MIDI Triggers and 可驱动肢体 / Drivable Limbs of the **components**; special actions are only saved into the .drumkit and take no part in generation. Add pitches and adjust limbs on the components instead.",
          ),
        },
        {
          kind: "p",
          text: l(
            "**.recorder 被改坏了怎么办？** 没有撤销。用 ⚡ 一键生成（MIDI → Recorder → 动画） 重新搜索一遍，会用一份新的最优方案覆盖它。",
            "**I broke the .recorder file — now what?** There is no undo. Run ⚡ Generate All (MIDI → Recorder → Animation) again: it searches a new optimal solution and overwrites the file.",
          ),
        },
        {
          kind: "p",
          text: l(
            "**音频转 MIDI 出来的结果和原曲对不上？** 起音检测只能给出「什么时候敲了一下」，音高是按下拉菜单的类型和映射随机分配的，所以它适合把节奏素材变成可用的鼓点，不适合还原原曲的鼓件细节。对不上时先调 🔇 噪声过滤 与 🎚 灵敏度，再逐个素材核对类型与 音符 / 权重。",
            "**The MIDI produced from audio does not match the original song.** Onset detection only reports when a hit happened; the pitch is assigned at random from the dropdown type and its mapping. It is meant to turn rhythmic material into usable drum hits, not to reconstruct a recording's drum detail. If it is off, tune 🔇 噪声过滤 / 🔇 Noise Filter and 🎚 灵敏度 / 🎚 Sensitivity first, then check each clip's type and its Note / Weight entries.",
          ),
        },
      ],
    },
  ],
};
